// src/pages/nfl/NFLShowdownOptimizer.jsx
// FULL DROP-IN — multiple showdown slates and shared player simulations
// Includes:
// - Player Groups UI (at least / at most / exactly N of selected players)
// - End-to-end wiring to /solve_showdown_stream (payload.groups)
// - pOWN% column before Proj on Cards, and a summed pOWN% total
// - Lineups list gains "Total pOWN%" column (shows cap if set)
// - Single Exposure/Teams/Stacks panel located next to Lineup Results (duplicate removed)
// - Player pool table contained in a scrollable panel

import React, { useEffect, useMemo, useRef, useState } from "react";
import API_BASE from "../../utils/api";

/* ----------------------------- helpers ----------------------------- */
const clamp = (v, lo = 0, hi = 1e9) => Math.max(lo, Math.min(hi, Number.isFinite(+v) ? +v : lo));
const num = (v) => {
  if (v == null) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const s = String(v).replace(/\$/g, "").replace(/[,  \s]/g, "").replace(/%/g, "").trim();
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};
const fmt0 = (n) => (Number.isFinite(n) ? n.toLocaleString() : "—");
const fmt1 = (n) => (Number.isFinite(n) ? (+n).toFixed(1) : "—");
const escapeCSV = (s) => { const v = String(s ?? ""); return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v; };

const geoMeanPct = (items, accessor) => {
  const vals = (items || [])
    .map((x) => Number(accessor(x)))
    .filter((v) => Number.isFinite(v) && v > 0);

  if (!vals.length) return 0;

  const logAvg = vals.reduce((sum, v) => sum + Math.log(v), 0) / vals.length;
  return Math.exp(logAvg);
};

/* relative time (mm ago) */
const timeAgo = (iso) => {
  try {
    const d = typeof iso === "string" ? new Date(iso) : new Date(iso ?? 0);
    const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    return `${h}h ago`;
  } catch { return ""; }
};

/* ----------------------------- UI THEME ----------------------------- */

const OPT_LIGHT_THEME = {
  page: "min-h-screen bg-slate-100 text-slate-900",
  headerText: "text-slate-900",
  subtext: "text-slate-600",
  card: "border border-slate-200 bg-slate-50 shadow-sm",
  pill: "bg-white ring-slate-300",
  pillDarkBtnActive: "bg-sky-500 text-slate-950 shadow-sm",
  pillDarkBtnIdle: "text-slate-500 hover:text-slate-800",
  pillLightBtnActive: "bg-sky-500 text-slate-950 shadow-sm",

  // ✅ was: "text-slate-400 hover:text-slate-800"
  pillLightBtnIdle: "text-slate-700 hover:text-slate-900",

  input: "bg-white border-slate-300 text-slate-800 placeholder:text-slate-400",
};

const OPT_DARK_THEME = {
  page: "min-h-screen bg-slate-950 text-slate-100",
  headerText: "text-slate-100",
  subtext: "text-slate-400",
  card: "border border-slate-800 bg-slate-900/95 shadow-[0_20px_60px_rgba(15,23,42,0.9)]",
  pill: "bg-slate-900/90 ring-slate-700/80",
  pillDarkBtnActive: "bg-sky-500 text-slate-950 shadow-sm",
  pillDarkBtnIdle: "text-slate-400 hover:text-slate-100",
  pillLightBtnActive: "bg-sky-500 text-slate-950 shadow-sm",
  pillLightBtnIdle: "text-slate-500 hover:text-slate-200",
  input: "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500",
};

// small design tokens (structure only — theme applied in-component)
const cls = {
  input: "h-8 px-2 text-sm rounded-md focus:ring-2 focus:ring-blue-200 focus:border-blue-400",
  btn: {
    primary: "h-8 px-4 rounded-md bg-blue-600 text-white hover:bg-blue-700",
    ghost: "h-8 px-3 rounded-md border text-sm",
    ghostSm: "h-7 px-2 text-xs rounded-md border whitespace-nowrap shrink-0",
    chip: "px-3 py-1.5 text-xs font-medium rounded-full border",
    iconSm: "inline-flex items-center justify-center w-5 h-5 rounded-md border text-[11px]",
  },
  card: "rounded-2xl",
  tableHead: "px-2 py-1 font-semibold text-[11px] leading-tight text-center select-none",
  cell: "px-2 py-1 text-center leading-tight",
};

const INPUT_BASE = "w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-500/30";


// persistent state
// Hydration guard prevents the initial/default render from overwriting a saved
// localStorage value before that saved value has been restored. This mirrors
// the NFL Classic optimizer behavior so refreshes preserve settings/constraints.
const useStickyState = (key, init) => {
  const read = (storageKey) => {
    try { const raw = localStorage.getItem(storageKey); return raw == null ? init : JSON.parse(raw); }
    catch { return init; }
  };
  const [record, setRecord] = useState(() => ({ key, value: read(key) }));
  const value = record.key === key ? record.value : read(key);
  useEffect(() => {
    if (record.key !== key) setRecord({ key, value: read(key) });
    else { try { localStorage.setItem(key, JSON.stringify(record.value)); } catch {} }
  }, [key, record]); // eslint-disable-line react-hooks/exhaustive-deps
  const setValue = (next) => setRecord((prev) => {
    const current = prev.key === key ? prev.value : read(key);
    return { key, value: typeof next === "function" ? next(current) : next };
  });
  return [value, setValue];
};

// persistent Set (stored as array under the hood)
const useStickySet = (key, initArr = []) => {
  const [arr, setArr] = useStickyState(key, initArr);
  const set = useMemo(() => new Set(arr), [arr]);
  const setSet = (updater) => {
    setArr((prev) => {
      const next = typeof updater === "function" ? updater(new Set(prev)) : updater;
      return Array.from(next);
    });
  };
  return [set, setSet];
};

/* ----------------------------- team meta --------------------------- */
const NFL_TEAMS = {
  ARI:{city:"Arizona",nickname:"Cardinals"}, ATL:{city:"Atlanta",nickname:"Falcons"},
  BAL:{city:"Baltimore",nickname:"Ravens"}, BUF:{city:"Buffalo",nickname:"Bills"},
  CAR:{city:"Carolina",nickname:"Panthers"}, CHI:{city:"Chicago",nickname:"Bears"},
  CIN:{city:"Cincinnati",nickname:"Bengals"}, CLE:{city:"Cleveland",nickname:"Browns"},
  DAL:{city:"Dallas",nickname:"Cowboys"}, DEN:{city:"Denver",nickname:"Broncos"},
  DET:{city:"Detroit",nickname:"Lions"}, GB:{city:"Green Bay",nickname:"Packers"},
  HOU:{city:"Houston",nickname:"Texans"}, IND:{city:"Indianapolis",nickname:"Colts"},
  JAX:{city:"Jacksonville",nickname:"Jaguars"}, KC:{city:"Kansas City",nickname:"Chiefs"},
  LAC:{city:"Los Angeles",nickname:"Chargers"}, LAR:{city:"Los Angeles",nickname:"Rams"},
  LV:{city:"Las Vegas",nickname:"Raiders"}, MIA:{city:"Miami",nickname:"Dolphins"},
  MIN:{city:"Minnesota",nickname:"Vikings"}, NE:{city:"New England",nickname:"Patriots"},
  NO:{city:"New Orleans",nickname:"Saints"}, NYG:{city:"New York",nickname:"Giants"},
  NYJ:{city:"New York",nickname:"Jets"}, PHI:{city:"Philadelphia",nickname:"Eagles"},
  PIT:{city:"Pittsburgh",nickname:"Steelers"}, SEA:{city:"Seattle",nickname:"Seahawks"},
  SF:{city:"San Francisco",nickname:"49ers"}, TB:{city:"Tampa Bay",nickname:"Buccaneers"},
  TEN:{city:"Tennessee",nickname:"Titans"}, WAS:{city:"Washington",nickname:"Commanders"},
};
const TEAM_COLORS = { ARI:"#97233F", ATL:"#A71930", BAL:"#241773", BUF:"#00338D", CAR:"#0085CA", CHI:"#0B162A",
  CIN:"#FB4F14", CLE:"#311D00", DAL:"#041E42", DEN:"#FB4F14", DET:"#0076B6", GB:"#203731",
  HOU:"#03202F", IND:"#002C5F", JAX:"#006778", KC:"#E31837", LAC:"#0080C6", LAR:"#003594",
  LV:"#000000", MIA:"#008E97", MIN:"#4F2683", NE:"#002244", NO:"#D3BC8D", NYG:"#0B2265",
  NYJ:"#125740", PHI:"#004C54", PIT:"#FFB612", SEA:"#002244", SF:"#AA0000", TB:"#D50A0A",
  TEN:"#0C2340", WAS:"#5A1414" };
const hexToRGB = (hex) => { const h = (hex || "#888").replace("#", ""); const v = parseInt(h, 16); return { r: (v>>16)&255, g:(v>>8)&255, b:v&255 }; };
const readableText = (hex) => { const {r,g,b} = hexToRGB(hex); const L = 0.2126*r + 0.7152*g + 0.0722*b; return L < 140 ? "#FFFFFF" : "#111111"; };
const TeamPill = ({ abbr, title }) => { const bg = TEAM_COLORS[abbr] || "#E5E7EB"; const fg = readableText(bg); return <span className="px-2 py-[1px] rounded text-[11px] leading-tight" style={{backgroundColor:bg,color:fg}} title={title || abbr}>{abbr||"—"}</span>; };

// Small helper to render an NFL team logo by abbr (tries .svg then .png)
const teamLogoPath = (abbr) => `/logos/nfl/${abbr}.svg`;

const TeamLogo = ({ abbr, className = "w-4 h-4" }) => {
  if (!abbr) return null;
  return (
    <img
      src={teamLogoPath(abbr)}
      alt={abbr}
      className={className}
      onError={(e) => {
        // fallback to .png if .svg is missing
        e.currentTarget.onerror = null;
        e.currentTarget.src = `/logos/nfl/${abbr}.png`;
      }}
    />
  );
};

/* --------- tag normalization for DK CPT vs FD MVP (state keys) ----- */
const makeTagKey = (capTag) => (name, tag) => `${name}::${(tag === "FLEX" ? "FLEX" : capTag)}`;

/* ------------------------------ data IO ---------------------------- */
const SOURCE = "/data/nfl/showdown/latest/projections.json";
const SITE_IDS_SOURCE = "/data/nfl/showdown/latest/site_ids.json";
const META = "/data/nfl/showdown/latest/meta.json";
const VEGAS_SOURCE = "/data/nfl/classic/latest/vegas.json";

function useJson(url, { autoMs = 0, enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [lastModified, setLastModified] = useState(null);

  const fetchOnce = async () => {
    try {
      setLoading(true);
      const res = await fetch(url, { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
      const ct = (res.headers.get("content-type") || "").toLowerCase();
      const body = ct.includes("application/json") ? await res.json() : JSON.parse(await res.text());
      setData(body);
      setErr(null);
      setFetchedAt(new Date());
      const lm = res.headers.get("last-modified");
      if (lm) setLastModified(new Date(lm));
    } catch (e) {
      setData(null);
      setErr(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    fetchOnce();
    let id = null;
    if (enabled && autoMs > 0) {
      id = setInterval(() => alive && fetchOnce(), autoMs);
    }
    return () => { alive = false; if (id) clearInterval(id); };
  }, [url, autoMs, enabled]);

  return { data, err, loading, fetchedAt, lastModified, refetch: fetchOnce };
}
// ---------- stable last-updated (no Date/fetchedAt fallback) ----------
function useStableLastUpdated({ dataUrl, metaUrl, artifactEndsWith } = {}) {
  const [d, setD] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    (async () => {
      // 1) Prefer Last-Modified on the data file
      try {
        const h = await fetch(dataUrl, { method: "HEAD", cache: "no-store" });
        const lm = h.headers.get("last-modified");
        if (alive && lm) { setD(new Date(lm)); return; }
      } catch {}
      // 2) Try GET (if LM missing on HEAD)
      try {
        const r = await fetch(`${dataUrl}?_=${Date.now()}`, { cache: "no-store" });
        const lm = r.headers.get("last-modified");
        if (alive && lm) { setD(new Date(lm)); return; }
      } catch {}
      // 3) Fallback to meta.json
      try {
        const m = await fetch(`${metaUrl}?_=${Date.now()}`, { cache: "no-store" }).then(x => x.json());
        let iso = m?.updated_iso || m?.updated_utc || m?.updated || m?.last_updated || null;
        if (!iso && Array.isArray(m?.artifacts) && artifactEndsWith) {
          const art = m.artifacts.find(a => String(a.path || "").endsWith(artifactEndsWith));
          iso = art?.last_updated || art?.tags?.last_updated || null;
        }
        if (alive && iso) {
          const dt = new Date(iso);
          if (!isNaN(dt)) setD(dt);
        }
      } catch {}
    })();
    return () => { alive = false; };
  }, [dataUrl, metaUrl, artifactEndsWith]);
  return d;
}


/* ----------------------------- API (SSE) --------------------------- */
async function solveStreamShowdown(payload, onItem, onDone) {
  const res = await fetch(`${API_BASE}/solve_showdown_stream`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
  });
  if (!res.ok || !res.body) throw new Error("Stream failed to start");
  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buf = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parts = buf.split("\n\n"); buf = parts.pop() ?? "";
    for (const chunk of parts) {
      const line = chunk.split("\n").find((l) => l.startsWith("data: "));
      if (!line) continue;
      try {
        const evt = JSON.parse(line.slice(6));
        if (evt.done) onDone?.(evt); else onItem?.(evt);
      } catch {}
    }
  }
}

/* ------------------------------ SITES ------------------------------ */
const SITES = {
  dk: { key: "dk", label: "DraftKings", logo: "/logos/dk.png", cap: 50000, capTag: "CPT", mvpFieldPrefix: "DK CPT", flexFieldPrefix: "DK FLEX" },
  fd: { key: "fd", label: "FanDuel",  logo: "/logos/fd.png", cap: 60000, capTag: "MVP", mvpFieldPrefix: "FD MVP", flexFieldPrefix: "FD FLEX" },
};

/* ------------- robust field getter (handles many spellings) -------- */
function pick(obj, variants, fallback = 0) {
  for (const v of variants) {
    if (obj == null) break;
    for (const k of Object.keys(obj)) {
      if (k.toLowerCase().replace(/[^a-z0-9]/g,"") === v.toLowerCase().replace(/[^a-z0-9]/g,"")) {
        return obj[k];
      }
    }
  }
  return fallback;
}

/* --------------------- name/pos/team normalization ----------------- */
function normName(s){return String(s||"").toLowerCase().replace(/\u2019/g,"'").replace(/\./g,"").replace(/,\s*(jr|sr)\b/g,"").replace(/\b(jr|sr)\b/g,"").replace(/[^a-z' -]/g,"").replace(/\s+/g," ").trim();}
function normTeam(s){ const t = String(s || "").toUpperCase().trim(); return t === "JAC" ? "JAX" : t === "WSH" ? "WAS" : t; }
function normPosRaw(s){return (s||"").toUpperCase().trim();}
function normPos(s){ const p = normPosRaw(s); return (p === "D" || p === "DEF") ? "DST" : p; }
function inferTeamFromNameForDST(name) {
  const nm = normName(name);

  for (const [abbr, t] of Object.entries(NFL_TEAMS)) {
    const nick = normName(t.nickname);
    const city = normName(t.city);
    const full = normName(`${t.city} ${t.nickname}`);

    if (nm === nick || nm === city || nm === full) {
      return abbr;
    }
  }

  return "";
}

// Shared data stays separate from site/slate-specific salary and upload IDs.
const SIMULATIONS_SOURCE = "/data/nfl/classic/latest/playersimulations.json";
const rowsOf = (data) => Array.isArray(data) ? data : data?.rows ?? data?.players ?? data?.data ?? [];

function showdownPlayerKey(name, team, pos = "") {
  const tm = normTeam(team);
  const dst = normPos(pos) === "DST" || (tm && inferTeamFromNameForDST(name) === tm);
  return `${tm}|${dst ? "DST" : normName(name)}`;
}

function availableShowdownSlates(data, site) {
  // Old JSON remains usable while the replacement exporter is being installed.
  const source = Array.isArray(data?.slates) ? data.slates
    : data && (data.dk?.length || data.fd?.length) ? [{ ...data, key: "legacy", label: "Current showdown" }] : [];
  return source.filter((s) => Array.isArray(s[site]) && s[site].length > 0)
    .slice().sort((a, b) => String(a.gameDate || "").localeCompare(String(b.gameDate || "")) || a.key.localeCompare(b.key));
}

// Interpret salary-file wall-clock timestamps in Eastern time, regardless of
// the user's browser timezone, including Eastern standard/daylight time.
function showdownKickoffMillis(slate) {
  const raw = String(slate?.gameDate || "").trim();
  if (!raw) return NaN;
  if (/(Z|[+-]\d{2}:?\d{2})$/i.test(raw)) return Date.parse(raw);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return NaN;
  const target = Date.UTC(+match[1], +match[2] - 1, +match[3], +match[4], +match[5], +(match[6] || 0));
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: slate.timezone || "America/New_York", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
  });
  let instant = target;
  for (let attempt = 0; attempt < 3; attempt++) {
    const parts = Object.fromEntries(formatter.formatToParts(new Date(instant)).map(({ type, value }) => [type, value]));
    const displayed = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
    const correction = target - displayed;
    if (!correction) return instant;
    instant += correction;
  }
  return instant;
}

function chooseShowdownSlate(slates, selection, now) {
  const choices = slates.map((slate) => ({ slate, kickoff: showdownKickoffMillis(slate) }))
    .sort((a, b) => (Number.isFinite(a.kickoff) ? a.kickoff : Infinity) - (Number.isFinite(b.kickoff) ? b.kickoff : Infinity));
  const manual = selection && choices.find(({ slate }) => slate.key === selection.key);
  // A future game selected manually advances once it locks. Selecting a game
  // that was already locked deliberately opens its history for review.
  if (manual && (!Number.isFinite(manual.kickoff) || now < manual.kickoff || selection.selectedAt >= manual.kickoff)) return manual.slate;
  const upcoming = choices.find(({ kickoff }) => Number.isFinite(kickoff) && kickoff > now);
  if (upcoming) return upcoming.slate;
  // If every game has locked, keep the latest game available for review.
  return choices.filter(({ kickoff }) => Number.isFinite(kickoff)).at(-1)?.slate || choices[0]?.slate || null;
}

function buildShowdownRows({ slate, site, simulations, projections, legacyIds, customProj }) {
  if (!slate) return [];
  const scheduleTeams = [slate.away, slate.home].filter(Boolean).map(normTeam);
  const records = slate[site] || [];
  const groups = new Map();
  for (const rec of records) {
    const tm = normTeam(rec.team);
    const k = showdownPlayerKey(rec.name, tm, rec.player_position || rec.pos);
    const g = groups.get(k) || { name: rec.name, team: tm, records: [] };
    g.records.push(rec); groups.set(k, g);
  }
  const index = (data) => new Map(rowsOf(data).map((r) => [
    showdownPlayerKey(r.player ?? r.name ?? r.Player, r.team ?? r.Team, r.pos ?? r.Pos), r,
  ]));
  const simMap = index(simulations), projMap = index(projections);
  const originalKeys = new Set((legacyIds?.[site] || []).map((r) => r.slate_key).filter(Boolean));
  // The old projections workbook only supplies rates for its original game.
  const originalGame = slate.key === "legacy" || originalKeys.has(slate.key);
  const pct = (v) => {
    if (v == null || String(v).trim() === "") return null;
    const n = num(v);
    return String(v).includes("%") || n > 1 ? n / 100 : n;
  };
  const output = [];
  for (const [k, group] of groups) {
    const sim = simMap.get(k), old = originalGame ? projMap.get(k) : null;
    const flex = site === "dk" ? group.records.find((r) => r.pos === "FLEX") : group.records[0];
    const cap = site === "dk" ? group.records.find((r) => r.pos === "CPT") : flex;
    if (!flex || !cap || !flex.id || !cap.id) continue;
    const salary = num(site === "dk" ? flex.salary : flex.salary_flex);
    const capSalary = num(site === "dk" ? cap.salary : cap.salary_mvp);
    if (salary <= 0 || capSalary <= 0) continue;
    const source = sim || old || {};
    const name = sim?.player ?? old?.player ?? old?.Player ?? group.name;
    const pos = normPos(source.pos ?? source.Pos ?? flex.player_position ?? flex.pos);
    const realPos = ["QB", "RB", "WR", "TE", "DST", "K"].includes(pos) ? pos : (k.endsWith("|DST") ? "DST" : "");
    const baseProj = num(pick(source, [`${site}_proj`, `${site} proj`, `${SITES[site].flexFieldPrefix} proj`], 0));
    const floor = num(pick(source, [`${site}_floor`, `${SITES[site].flexFieldPrefix} floor`], 0));
    const ceil = num(pick(source, [`${site}_ceiling`, `${site}_ceil`, `${SITES[site].flexFieldPrefix} ceiling`], 0));
    const capTag = SITES[site].capTag;
    const slotRate = (field, isCap) => {
      if (!old) return null;
      const prefix = isCap ? SITES[site].mvpFieldPrefix : SITES[site].flexFieldPrefix;
      const tag = capTag.toLowerCase();
      const fields = isCap
        ? [`${site}_${tag}_${field}`, `${site}_${tag}_${field}%`, `${prefix} ${field}%`, `${prefix} ${field}`]
        : [`${site}_${field}`, `${site}_${field}%`, `${prefix} ${field}%`, `${prefix} ${field}`];
      return pct(pick(old, fields, null));
    };
    const customFlex = customProj?.[`${name}::FLEX`];
    const customCap = customProj?.[`${name}::${capTag}`];
    const hasFlexCustom = customFlex != null && String(customFlex).trim() !== "";
    const hasCapCustom = customCap != null && String(customCap).trim() !== "";
    // Captain/MVP receives 1.5x points. Salaries always come from the salary file.
    const baseCapProj = baseProj * 1.5;
    output.push({
      name, team: group.team, pos: realPos,
      opp: scheduleTeams.find((tm) => tm !== group.team) || source.opp || source.Opp || "",
      salary, cap_salary: capSalary,
      proj: hasFlexCustom ? Math.max(0, num(customFlex)) : baseProj,
      cap_proj: hasCapCustom ? Math.max(0, num(customCap)) : baseCapProj,
      base_proj: baseProj, base_cap_proj: baseCapProj,
      custom_proj: hasFlexCustom ? customFlex : "", custom_cap_proj: hasCapCustom ? customCap : "",
      has_custom_proj: hasFlexCustom, has_custom_cap_proj: hasCapCustom,
      floor, ceil, cap_floor: floor * 1.5, cap_ceil: ceil * 1.5,
      pown: slotRate("pown", false), opt: slotRate("opt", false),
      cap_pown: slotRate("pown", true), cap_opt: slotRate("opt", true),
      hasProjection: !!(sim || old) && baseProj > 0,
      siteIdOnly: !(sim || old) || baseProj <= 0,
      siteId: String(flex.id), capSiteId: String(cap.id),
      siteName: flex.name, capSiteName: cap.name,
      fdPrefix: slate.fd_prefix || flex.prefix || null,
      slateKey: slate.key, __raw: source,
    });
  }
  return output;
}

// Missing rates elsewhere in the pool do not hide a complete lineup's rates.
function showdownLineupRates(lineup, rowsMap) {
  const names = Array.isArray(lineup?.players) ? lineup.players : [];
  const own = [], optimal = [];
  let ownComplete = names.length === 6, optComplete = names.length === 6;
  names.forEach((name, i) => {
    const row = rowsMap.get(name);
    const pown = i === 0 ? row?.cap_pown : row?.pown;
    const opt = i === 0 ? row?.cap_opt : row?.opt;
    if (pown == null || !Number.isFinite(pown)) ownComplete = false;
    else own.push(pown * 100);
    if (opt == null || !Number.isFinite(opt)) optComplete = false;
    else optimal.push(opt * 100);
  });
  return {
    ownComplete, optComplete,
    totalPown: ownComplete ? own.reduce((sum, v) => sum + v, 0) : null,
    geoMeanPown: ownComplete ? geoMeanPct(own, (v) => v) : null,
    totalOpt: optComplete ? optimal.reduce((sum, v) => sum + v, 0) : null,
  };
}

function showdownCSV({ lineups, site, rows, slate, detailed = false }) {
  if (!slate || !lineups?.length) throw new Error("No lineups to export.");
  const slots = site === "dk" ? ["CPT", ...Array(5).fill("FLEX")]
    : ["MVP - 1.5X Points", ...Array(5).fill("AnyFLEX")];
  const header = detailed ? ["Lineup #", "Site", "Slate", "Salary", "Total Proj", "Total pOWN%", "Total Opt%", ...slots] : slots;
  const out = [header.map(escapeCSV).join(",")];
  for (const [i, L] of lineups.entries()) {
    if (L.slateKey !== slate.key || L.site !== site) throw new Error("This build belongs to a different slate or site. Select its original slate before exporting.");
    const rowsMap = new Map((L.exportRows || rows).map((r) => [r.name, r]));
    const names = orderShowdownNamesByFlexSalary(L.players, rowsMap);
    if (names.length !== 6 || new Set(names).size !== 6) throw new Error(`Lineup ${i + 1} must contain six different players.`);
    let salary = 0, proj = 0, pown = 0, opt = 0, hasOwn = true, hasOpt = true;
    const cells = names.map((name, slot) => {
      const r = rowsMap.get(name);
      if (!r || r.slateKey !== slate.key) throw new Error(`Missing selected-slate data for ${name}.`);
      const cap = slot === 0;
      const id = String(cap ? r.capSiteId : r.siteId).trim();
      if (!id || id === "undefined" || id === "null") throw new Error(`Missing ${cap ? "captain/MVP" : "flex"} ID for ${name}.`);
      salary += cap ? r.cap_salary : r.salary;
      proj += cap ? r.cap_proj : r.proj;
      const own = cap ? r.cap_pown : r.pown, optimal = cap ? r.cap_opt : r.opt;
      if (own == null) hasOwn = false; else pown += own * 100;
      if (optimal == null) hasOpt = false; else opt += optimal * 100;
      const siteName = (cap ? r.capSiteName : r.siteName) || name;
      if (site === "dk") return `${siteName} (${id})`;
      // FD salary downloads commonly already contain the full group-player ID.
      const fullId = id.includes("-") || !r.fdPrefix ? id : `${r.fdPrefix}-${id}`;
      return `${fullId}:${siteName}`;
    });
    const values = detailed ? [i + 1, site.toUpperCase(), slate.label, salary, proj.toFixed(1), hasOwn ? pown.toFixed(1) : "", hasOpt ? opt.toFixed(1) : "", ...cells] : cells;
    out.push(values.map(escapeCSV).join(","));
  }
  return out.join("\r\n");
}

function downloadShowdownCSV(args) {
  try {
    const csv = showdownCSV(args);
    const filename = `${args.site}_${args.slate.key}_showdown_${args.detailed ? "detailed" : "ids"}.csv`;
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) { alert(e.message); }
}

/* ============================== page =============================== */
/* ---------------------- player game logs ---------------------- */
const { GameLogModal: OptimizerGameLogModal, useLazyPreparedGameLogs: useOptimizerGameLogs, GAME_LOG_YEARS: OPT_GAME_LOG_YEARS } = (() => {
  const LOGO_BASE = "/logos/nfl";
  const LOGO_EXT = "png";
  const SHOW_TEAM_LOGOS = true;
  const TEAM_ABBR_MAP = { WSH: "WAS", JAC: "JAX", OAK: "LV", SD: "LAC", STL: "LAR" };
  const keynorm = (value) => String(value ?? "").trim().toLowerCase().replace(/[%]/g, "pct").replace(/[\s._-]/g, "").replace(/[()]/g, "");
function fixAbbr(code) {
  const c = String(code || "").toUpperCase();
  return TEAM_ABBR_MAP[c] || c;
}
function TeamWithLogo({ code }) {
  const c = fixAbbr(code);
  if (!c) return null;
  if (!SHOW_TEAM_LOGOS) return <span>{c}</span>;
  const src = `${LOGO_BASE}/${c}.${LOGO_EXT}`;
  return (
    <span className="inline-flex items-center gap-1">
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <img
        src={src}
        className="h-4 w-4 shrink-0"
        onError={(e) => (e.currentTarget.style.display = "none")}
      />
      <span>{c}</span>
    </span>
  );
}
// Prepared game-log files are generated by the NFL stat puller.
// Add a season here when that season folder is available under /public/data/nfl/std/.
const GAME_LOG_YEARS = [2026, 2025];
const DEFAULT_GAME_LOG_YEAR = 2026;

const opportunityUrl = (year) => `/data/nfl/std/${year}/ff_opportunity.json`;

function gameLogUrl(year, position) {
  return `/data/nfl/std/${year}/game_logs_${String(position || "QB").toLowerCase()}.json`;
}

// Cache by season + position so changing positions or years does not redownload files.
const GAME_LOG_PREPARED_CACHE = new Map();
const GAME_LOG_REQUEST_CACHE = new Map();


async function loadPreparedGameLogs(position, year) {
  const posKey = String(position || "QB").toUpperCase();
  const season = Number(year) || DEFAULT_GAME_LOG_YEAR;
  const cacheKey = `${season}:${posKey}`;

  if (GAME_LOG_PREPARED_CACHE.has(cacheKey)) {
    return GAME_LOG_PREPARED_CACHE.get(cacheKey);
  }
  if (GAME_LOG_REQUEST_CACHE.has(cacheKey)) {
    return GAME_LOG_REQUEST_CACHE.get(cacheKey);
  }

  const request = Promise.all([
    // The main log is required; the opportunity file may be absent for older seasons.
    fetch(gameLogUrl(season, posKey), { cache: "no-store" }).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    }),
    fetch(opportunityUrl(season), { cache: "no-store" })
      .then((response) => response.ok ? response.json() : [])
      .catch(() => []),
  ])
    .then(([json, opportunityJson]) => {
      const rows = Array.isArray(json) ? json : json?.rows || json?.data || [];
      const opportunityRows = Array.isArray(opportunityJson)
        ? opportunityJson : opportunityJson?.rows || opportunityJson?.data || [];
      const prepared = mergeOpportunityIntoGameLogs(rows, opportunityRows, posKey);
      GAME_LOG_PREPARED_CACHE.set(cacheKey, prepared);
      GAME_LOG_REQUEST_CACHE.delete(cacheKey);
      return prepared;
    })
    .catch((error) => {
      GAME_LOG_REQUEST_CACHE.delete(cacheKey);
      throw error;
    });

  GAME_LOG_REQUEST_CACHE.set(cacheKey, request);
  return request;
}

function useLazyPreparedGameLogs(position, year, enabled) {
  const posKey = String(position || "QB").toUpperCase();
  const season = Number(year) || DEFAULT_GAME_LOG_YEAR;
  const cacheKey = `${season}:${posKey}`;
  const [rows, setRows] = useState(() => GAME_LOG_PREPARED_CACHE.get(cacheKey) || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    const cached = GAME_LOG_PREPARED_CACHE.get(cacheKey);

    if (cached) {
      setRows(cached);
      setLoading(false);
      setError("");
      return () => { alive = false; };
    }

    setRows([]);
    if (!enabled) {
      setLoading(false);
      setError("");
      return () => { alive = false; };
    }

    setLoading(true);
    setError("");
    loadPreparedGameLogs(posKey, season)
      .then((prepared) => { if (alive) setRows(prepared); })
      .catch((e) => { if (alive) setError(String(e)); })
      .finally(() => { if (alive) setLoading(false); });

    return () => { alive = false; };
  }, [cacheKey, posKey, season, enabled]);

  return { rows, loading, error };
}


const NFL_SUFFIXES = new Set(["JR", "SR", "II", "III", "IV", "V"]);

function normalizePlayerName(name = "") {
  const parts = String(name)
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);
  if (parts.length > 1 && NFL_SUFFIXES.has(parts[parts.length - 1])) parts.pop();
  return parts.join(" ");
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function firstValue(row, keys, fallback = "") {
  for (const k of keys) {
    if (row?.[k] !== undefined && row?.[k] !== null && row?.[k] !== "") return row[k];
  }

  // Feeds use a mix of snake_case, spaced labels, and uppercase headers.
  // Try a normalized-key lookup before giving up.
  if (row && typeof row === "object") {
    const normalized = new Map(Object.keys(row).map((key) => [keynorm(key), key]));
    for (const k of keys) {
      const actual = normalized.get(keynorm(k));
      if (actual && row[actual] !== undefined && row[actual] !== null && row[actual] !== "") return row[actual];
    }
  }
  return fallback;
}


function nullableNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(String(value).replace(/[%,$]/g, "").trim());
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizePreparedGameLogRow(row = {}, fallbackPosition = "") {
  const position = String(firstValue(row, ["position", "pos"], fallbackPosition)).toUpperCase();
  const name = firstValue(row, ["player", "name", "player_name"], "");
  const team = fixAbbr(firstValue(row, ["team", "posteam", "recent_team"], ""));

  return {
    raw: row,
    season: num(firstValue(row, ["season"], 0)),
    week: num(firstValue(row, ["week"], 0)),
    seasonType: String(firstValue(row, ["season_type", "game_type"], "REG")),
    playerId: String(firstValue(row, ["player_id", "player_gsis_id", "gsis_id"], "") || "").trim().toLowerCase(),
    name,
    pos: position,
    team,
    opp: fixAbbr(firstValue(row, ["opponent", "opp", "opponent_team"], "")),
    result: String(firstValue(row, ["result"], "")),
    teamScore: firstValue(row, ["team_score"], null),
    opponentScore: firstValue(row, ["opponent_score"], null),
    _playerKey: normalizePlayerName(firstValue(row, ["player_key", "player", "name"], name)),

    passCmp: num(firstValue(row, ["pass_completions", "passCmp"], 0)),
    passAtt: num(firstValue(row, ["pass_attempts", "passAtt"], 0)),
    passYds: num(firstValue(row, ["passing_yards", "passYds"], 0)),
    passTd: num(firstValue(row, ["passing_tds", "passTd"], 0)),
    interceptions: num(firstValue(row, ["interceptions"], 0)),
    compPct: num(firstValue(row, ["completion_pct", "compPct"], 0)),
    ypa: num(firstValue(row, ["yards_per_attempt", "ypa"], 0)),
    sacks: num(firstValue(row, ["sacks"], 0)),

    rushAtt: num(firstValue(row, ["rushing_attempts", "rushAtt"], 0)),
    rushYds: num(firstValue(row, ["rushing_yards", "rushYds"], 0)),
    rushTd: num(firstValue(row, ["rushing_tds", "rushTd"], 0)),
    ypc: num(firstValue(row, ["yards_per_carry", "ypc"], 0)),

    targets: num(firstValue(row, ["targets"], 0)),
    rec: num(firstValue(row, ["receptions", "rec"], 0)),
    recYds: num(firstValue(row, ["receiving_yards", "recYds"], 0)),
    recTd: num(firstValue(row, ["receiving_tds", "recTd"], 0)),
    ypr: num(firstValue(row, ["yards_per_reception", "ypr"], 0)),
    airYards: num(firstValue(row, ["air_yards", "airYards"], 0)),
    targetShare: firstValue(row, ["target_share", "targetShare"], null),
    airShare: firstValue(row, ["air_yards_share", "airShare"], null),
    snapPct: firstValue(row, ["snap_pct", "snapPct"], null),
    fumblesLost: num(firstValue(row, ["fumbles_lost", "fumblesLost"], 0)),
    dk: num(firstValue(row, ["dk_points", "dk"], 0)),
    fd: num(firstValue(row, ["fd_points", "fd"], 0)),

    // Filled from ff_opportunity.json when available.
    xFp: null,
    fpDiff: null,
    opportunities: null,

    pointsAllowed: num(firstValue(row, ["points_allowed"], 0)),
    fumbleRecoveries: num(firstValue(row, ["fumble_recoveries"], 0)),
    defensiveTds: num(firstValue(row, ["defensive_tds"], 0)),
  };
}

function mergeOpportunityIntoGameLogs(logRows = [], opportunityRows = [], fallbackPosition = "") {
  const prepared = logRows.map((row) => normalizePreparedGameLogRow(row, fallbackPosition));

  const byId = new Map();
  const byName = new Map();

  for (const raw of opportunityRows || []) {
    const season = num(firstValue(raw, ["season"], 0));
    const week = num(firstValue(raw, ["week"], 0));
    if (!season || !week) continue;

    const playerId = String(
      firstValue(raw, ["player_id", "player_gsis_id", "gsis_id"], "") || ""
    ).trim().toLowerCase();

    const name = normalizePlayerName(
      firstValue(raw, ["full_name", "player", "player_name", "name"], "")
    );
    const team = fixAbbr(firstValue(raw, ["posteam", "team", "recent_team"], ""));

    const entry = {
      xFp: nullableNumber(
        firstValue(raw, ["total_fantasy_points_exp", "fantasy_points_exp", "xfp"], null)
      ),
      fpDiff: nullableNumber(
        firstValue(raw, ["total_fantasy_points_diff", "fantasy_points_diff", "fpoe"], null)
      ),
      opportunities:
        num(firstValue(raw, ["rush_attempt", "rushing_attempts"], 0)) +
        num(firstValue(raw, ["rec_attempt", "targets"], 0)),
    };

    if (playerId) {
      byId.set(`${season}__${week}__${playerId}`, entry);
    }

    if (name) {
      byName.set(`${season}__${week}__${team}__${name}`, entry);
      byName.set(`${season}__${week}____${name}`, entry);
    }
  }

  return prepared.map((row) => {
    let opportunity = null;

    if (row.playerId) {
      opportunity = byId.get(`${row.season}__${row.week}__${row.playerId}`) || null;
    }

    if (!opportunity) {
      opportunity =
        byName.get(`${row.season}__${row.week}__${row.team}__${row._playerKey}`) ||
        byName.get(`${row.season}__${row.week}____${row._playerKey}`) ||
        null;
    }

    if (!opportunity) return row;

    return {
      ...row,
      xFp: opportunity.xFp,
      fpDiff: opportunity.fpDiff,
      opportunities: opportunity.opportunities,
    };
  });
}

function pct(v) {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  const n = Number(v);
  const x = Math.abs(n) <= 1.5 ? n * 100 : n;
  return `${x.toFixed(1).replace(/\.0$/, "")}%`;
}

function one(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(1).replace(/\.0$/, "") : "—";
}



function GameLogModal({ player, position, preparedRows = [], loading, error, onClose, isDark, gameLogYear, gameLogYears, onGameLogYearChange }) {
  const logs = useMemo(() => {
    const wanted = normalizePlayerName(player);
    return preparedRows
      .filter((r) => {
        const rowKey = r._playerKey || normalizePlayerName(
          firstValue(r, ["player_key", "player", "name", "full_name", "player_display_name"], "")
        );
        const rowPos = String(firstValue(r, ["pos", "position", "player_position"], "")).toUpperCase();
        return rowKey === wanted && (!rowPos || rowPos === position || (position === "RB" && rowPos === "FB"));
      })
      .sort((a, b) => a.season - b.season || a.week - b.week);
  }, [preparedRows, player, position]);

  useEffect(() => {
    const fn = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);

  const logNumber = (value) => value == null || value === "" ? "—" : one(value);
  const avg = (k) => {
    const values = logs.filter((r) => r[k] != null && r[k] !== "").map((r) => Number(r[k])).filter(Number.isFinite);
    return values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : null;
  };
  const signed = (v) => {
    if (v == null || !Number.isFinite(Number(v))) return "—";
    const n = Number(v);
    const body = Math.abs(n).toFixed(1).replace(/\.0$/, "");
    return n > 0 ? `+${body}` : n < 0 ? `-${body}` : "0";
  };
  const fpoeCell = (v) => {
    if (v == null || !Number.isFinite(Number(v))) return <span className="text-slate-500">—</span>;
    const n = Number(v);
    const tone = n > 0.05 ? (isDark ? "text-emerald-400" : "text-emerald-700") : n < -0.05 ? (isDark ? "text-rose-400" : "text-rose-700") : C.muted;
    return <span className={`font-extrabold ${tone}`}>{signed(n)}</span>;
  };
  const team = logs[0]?.team || "";
  const C = isDark
    ? { shell: "bg-slate-950 border-slate-700 text-slate-100", muted: "text-slate-400", head: "bg-slate-900", row: "border-slate-800 hover:bg-sky-500/10", card: "bg-slate-900/70 border-slate-800" }
    : { shell: "bg-white border-slate-300 text-slate-900", muted: "text-slate-500", head: "bg-slate-100", row: "border-slate-200 hover:bg-sky-50", card: "bg-slate-50 border-slate-200" };

  const common = [
    ["Season", r => r.season],
    ["Wk", r => r.week],
    ["Opp", r => (
      <div className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap leading-none">
        <TeamWithLogo code={r.opp} />
        {r.result && Number.isFinite(r.teamScore) && Number.isFinite(r.opponentScore) ? (
          <span
            className={`text-[9px] font-extrabold ${
              r.result === "W"
                ? "text-emerald-400"
                : r.result === "L"
                  ? "text-red-400"
                  : "text-slate-400"
            }`}
            title={`${r.result} ${r.teamScore}-${r.opponentScore}`}
          >
            {r.result} {r.teamScore}–{r.opponentScore}
          </span>
        ) : null}
      </div>
    )],
  ];
  const fantasy = [["DK", r => logNumber(r.dk)], ["FD", r => logNumber(r.fd)], ["xFP", r => r.xFp == null ? "—" : logNumber(r.xFp)], ["FPOE", r => fpoeCell(r.fpDiff)]];
  const passing = [
    ["Cmp", (r) => r.passCmp], ["Att", (r) => r.passAtt], ["Cmp%", (r) => pct(r.compPct)],
    ["Yds", (r) => r.passYds], ["TD", (r) => r.passTd],
    ["INT", (r) => r.interceptions], ["YPA", (r) => logNumber(r.ypa)],
  ];
  const rushing = [
    ["Att", (r) => r.rushAtt], ["Yds", (r) => r.rushYds], ["TD", (r) => r.rushTd],
    ...(position === "RB" ? [["YPC", (r) => logNumber(r.ypc)]] : []),
  ];
  const receiving = [
    ["Tgt", (r) => r.targets], ["Rec", (r) => r.rec], ["Yds", (r) => r.recYds],
    ["TD", (r) => r.recTd], ["YPR", (r) => logNumber(r.ypr)],
    ...(position !== "RB" ? [["Air Yds", (r) => r.airYards]] : []),
  ];
  const usage = [
    ...(position === "RB" ? [["Rush + Tgts", (r) =>
      r.opportunities == null ? r.rushAtt + r.targets : r.opportunities,
      "Rushing attempts plus targets"]] : []),
    ["Tgt%", (r) => pct(r.targetShare), "Share of team targets"],
    ...(position !== "RB" ? [["Air%", (r) => pct(r.airShare), "Share of team air yards"]] : []),
    ["Snap%", (r) => pct(r.snapPct), "Offensive snap share"],
  ];
  const groups = [
    { label: "Game", tone: isDark ? "text-slate-400 bg-slate-800/50" : "text-slate-600 bg-slate-200/60", columns: common },
    ...(position === "QB" ? [
      { label: "Passing", tone: isDark ? "text-sky-300 bg-sky-500/10" : "text-sky-700 bg-sky-500/10", columns: passing },
      { label: "Rushing", tone: isDark ? "text-emerald-300 bg-emerald-500/10" : "text-emerald-700 bg-emerald-500/10", columns: rushing },
    ] : [
      ...(position === "RB" ? [{ label: "Rushing", tone: isDark ? "text-emerald-300 bg-emerald-500/10" : "text-emerald-700 bg-emerald-500/10", columns: rushing }] : []),
      { label: "Receiving", tone: isDark ? "text-violet-300 bg-violet-500/10" : "text-violet-700 bg-violet-500/10", columns: receiving },
      { label: "Usage", tone: isDark ? "text-slate-300 bg-slate-800/60" : "text-slate-600 bg-slate-200/60", columns: usage },
    ]),
    { label: "Fantasy", tone: isDark ? "text-amber-300 bg-amber-500/10" : "text-amber-700 bg-amber-500/10", columns: fantasy },
  ];
  const columns = groups.flatMap((group) => group.columns.map(([label, render, title], index) => ({
    label, render, title: title || `${group.label}: ${label}`, group: group.label, start: index === 0,
  })));

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-2 md:p-5" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-label={`${player} game log`} className={`w-full max-w-[1500px] max-h-[92vh] flex flex-col overflow-hidden rounded-2xl border shadow-2xl ${C.shell}`}>
        <div className="flex shrink-0 flex-wrap items-start justify-between gap-4 border-b border-current/10 px-4 py-3 md:px-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {team ? <img src={`${LOGO_BASE}/${team}.${LOGO_EXT}`} className="h-7 w-7 object-contain" onError={(e) => (e.currentTarget.style.display = "none")} /> : null}
              <h2 className="truncate text-lg md:text-2xl font-extrabold">{player}</h2>
              <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-xs font-bold text-sky-400">{position}</span>
            </div>
            <p className={`mt-0.5 text-xs ${C.muted}`}>{gameLogYear} season / Weekly game log / Week 1 first</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <label className={`flex h-9 items-center gap-2 rounded-lg border px-3 ${C.card}`}>
              <span className={`text-[10px] font-bold uppercase tracking-wide ${C.muted}`}>Season</span>
              <select
                value={gameLogYear}
                onChange={(e) => onGameLogYearChange(Number(e.target.value))}
                className={
                  "bg-transparent text-sm font-bold outline-none " +
                  (isDark ? "text-slate-100" : "text-slate-900")
                }
              >
                {gameLogYears.map((year) => (
                  <option
                    key={year}
                    value={year}
                    className={isDark ? "bg-slate-900 text-slate-100" : "bg-white text-slate-900"}
                  >
                    {year}
                  </option>
                ))}
              </select>
            </label>
            <button onClick={onClose} className={`grid h-9 w-9 shrink-0 place-items-center rounded-full border ${C.card}`} aria-label="Close game log">✕</button>
          </div>
        </div>

        <div className="grid shrink-0 grid-cols-2 gap-2 px-3 py-3 sm:grid-cols-4 md:px-5">
          {[
            ["Games", logs.length],
            ["Avg DK", logNumber(avg("dk"))],
            ["Avg xFP", avg("xFp") == null ? "—" : logNumber(avg("xFp"))],
            [position === "QB" ? "Avg Pass Yds" : position === "RB" ? "Rush + Tgts/G" : "Avg Targets", position === "QB" ? logNumber(avg("passYds")) : position === "RB" ? one(logs.length ? logs.reduce((sum, r) => sum + Number(r.opportunities == null ? r.rushAtt + r.targets : r.opportunities), 0) / logs.length : 0) : logNumber(avg("targets"))],
          ].map(([label, value]) => (
            <div key={label} className={`rounded-xl border px-3 py-2 ${C.card}`}>
              <div className={`text-[10px] font-bold uppercase tracking-wide ${C.muted}`}>{label}</div>
              <div className="text-lg font-extrabold leading-tight">{loading ? "—" : value}</div>
            </div>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-auto border-t border-current/10">
          {loading ? <div className={`p-8 text-center text-sm ${C.muted}`}>Loading game logs…</div> : error ? <div className="p-8 text-center text-sm text-red-400">Unable to load game logs. Please try again.</div> : logs.length === 0 ? <div className={`p-8 text-center text-sm ${C.muted}`}>No weekly game logs found for this player.</div> : (
            <table className="w-max min-w-full border-separate border-spacing-0 text-[11px] tabular-nums md:text-xs">
              <caption className="sr-only">{player} weekly game log for {gameLogYear}, earliest week first</caption>
              <thead className={`sticky top-0 z-20 ${C.head}`}>
                <tr>
                  {groups.map((group, index) => (
                    <th key={group.label} scope="colgroup" colSpan={group.columns.length}
                      className={`h-8 border-b border-current/10 px-3 text-center text-[10px] font-extrabold uppercase tracking-[0.14em] ${group.tone} ${index ? (isDark ? "border-l border-l-slate-700" : "border-l border-l-slate-300") : ""}`}>
                      {group.label}
                    </th>
                  ))}
                </tr>
                <tr>
                  {columns.map(({ label, title, group, start }, i) => (
                    <th key={`${group}-${label}-${i}`} scope="col" title={title}
                      className={`h-9 whitespace-nowrap border-b ${isDark ? "border-slate-700" : "border-slate-300"} ${C.head} px-3 text-center font-bold ${start && i ? (isDark ? "border-l border-l-slate-700" : "border-l border-l-slate-300") : ""} ${
                        label === "DK" || label === "FD" ? (isDark ? "text-sky-400" : "text-sky-700") :
                        label === "xFP" || label === "FPOE" ? (isDark ? "text-amber-300" : "text-amber-700") : (isDark ? "text-slate-300" : "text-slate-600")
                      }`}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((r, ri) => (
                  <tr key={`${r.season}-${r.week}-${ri}`} className={`${ri % 2 ? (isDark ? "bg-slate-900/40" : "bg-slate-50") : ""} transition-colors ${C.row}`}>
                    {columns.map(({ label, render, group, start }, ci) => (
                      <td key={`${group}-${label}-${ci}`}
                        className={`h-10 whitespace-nowrap border-b ${isDark ? "border-slate-800/70" : "border-slate-200"} px-3 text-center ${start && ci ? (isDark ? "border-l border-l-slate-700" : "border-l border-l-slate-300") : ""} ${
                          label === "DK" || label === "FD" ? (isDark ? "bg-sky-500/[0.04] font-extrabold text-sky-400" : "bg-sky-500/[0.04] font-extrabold text-sky-700") :
                          label === "xFP" ? (isDark ? "bg-amber-500/[0.04] font-extrabold text-amber-300" : "bg-amber-500/[0.04] font-extrabold text-amber-700") : ""
                        }`}>
                        {render(r) ?? "—"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className={`flex shrink-0 flex-wrap items-center justify-between gap-x-6 gap-y-1 border-t border-current/10 px-4 py-2.5 text-[10px] leading-relaxed md:px-5 ${C.card} ${C.muted}`}>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span><span className={`font-bold ${isDark ? "text-amber-300" : "text-amber-700"}`}>xFP</span> Expected fantasy points</span>
            <span><span className={`font-bold ${isDark ? "text-amber-300" : "text-amber-700"}`}>FPOE</span> Fantasy points over expected</span>
            <span><span className="text-emerald-400">+ Above</span> / <span className="text-rose-400">− Below</span> expected</span>
          </div>
          <span>Esc to close</span>
        </div>
      </div>
    </div>
  );
}


return { GameLogModal, useLazyPreparedGameLogs, GAME_LOG_YEARS };
})();

export default function NFLShowdownOptimizer() {
const { data: projectionData } = useJson(SOURCE, { enabled: true });
const simulationFeed = useJson(SIMULATIONS_SOURCE, { enabled: true });
const data = projectionData;
const err = simulationFeed.err;
const loading = simulationFeed.loading;

const meta =
  useJson(SITE_IDS_SOURCE, { enabled: true });

const { data: vegasData } =
  useJson(VEGAS_SOURCE, { enabled: true });

const projectionsUpdatedAt = useStableLastUpdated({
  dataUrl: SIMULATIONS_SOURCE,
  metaUrl: "/data/nfl/latest/meta.json",
  artifactEndsWith: "/data/nfl/classic/latest/playersimulations.json",
});

  const ownershipUpdatedAt = useStableLastUpdated({
    dataUrl: SOURCE,
    metaUrl: META,
    artifactEndsWith: SOURCE,
  });


  const [theme, setTheme] = useStickyState("nflShow.theme", "dark");
  const isDark = theme === "dark";
  const T = isDark ? OPT_DARK_THEME : OPT_LIGHT_THEME;

  const [gameLogPlayer, setGameLogPlayer] = useState(null);
  const [gameLogYear, setGameLogYear] = useState(2026);
  const {
    rows: preparedGameLogRows,
    loading: gameLogsLoading,
    error: gameLogsError,
  } = useOptimizerGameLogs(gameLogPlayer?.pos || "QB", gameLogYear, Boolean(gameLogPlayer));


  
  // table readability helpers (dark mode)
  const tableText = isDark ? "text-slate-100" : "text-slate-900";
  const stripeRow = isDark ? "odd:bg-slate-950 even:bg-slate-900/70" : "odd:bg-white even:bg-gray-50";
  const totalsRow = isDark ? "bg-slate-800/60 text-slate-100 font-medium" : "bg-gray-50 font-medium";
  const theadBg = isDark ? "bg-slate-800/60" : "bg-gray-50";
const inputClass = `${INPUT_BASE} ${T.input}`;
  const ghostBtnClass = `${cls.btn.ghost} ${
    isDark
      ? "border-slate-700 bg-slate-900/80 text-slate-100 hover:bg-slate-800"
      : "border-gray-200 bg-white text-slate-800 hover:bg-gray-50"
  }`;
  const tableInputClass =
  "h-6 px-2 text-[11px] rounded-md border outline-none focus:ring-2 focus:ring-sky-500/30 " +
  (isDark
    ? "bg-slate-950/60 border-slate-700 text-slate-100"
    : "bg-white border-gray-200 text-slate-800");

  const ghostSmBtnClass = `${cls.btn.ghostSm} ${
    isDark
      ? "border-slate-700 bg-slate-900/80 text-slate-100 hover:bg-slate-800"
      : "border-gray-200 bg-white text-slate-800 hover:bg-gray-50"
  }`;
  const chipBtnClass = `${cls.btn.chip} ${
    isDark
      ? "border-slate-700 bg-slate-900/80 text-slate-100 hover:bg-slate-800"
      : "border-gray-200 bg-white text-slate-700 hover:bg-gray-50"
  }`;
  const iconBtnClass = `${cls.btn.iconSm} ${
    isDark
      ? "border-slate-700 bg-slate-900/80 text-slate-100 hover:bg-slate-800"
      : "border-gray-300 bg-white text-slate-800 hover:bg-gray-50"
  }`;

  const tableWrapClass = "overflow-hidden";

  const theadClass = isDark
    ? "bg-slate-900 sticky top-0 z-10 shadow-[0_1px_0_rgba(51,65,85,0.6)]"
    : "bg-gray-50 sticky top-0 z-10 shadow-[0_1px_0_#e5e7eb]";

  const rowClass = isDark
    ? "odd:bg-slate-900/70 even:bg-slate-950/40 hover:bg-sky-900/30 transition-colors"
    : "odd:bg-white even:bg-gray-50 hover:bg-blue-50/60 transition-colors";
  const [site, setSite] = useStickyState("sd.site", "dk");
  const cfg = SITES[site];
  const availableSlates = useMemo(() => availableShowdownSlates(meta.data, site), [meta.data, site]);
  const [nowTick, setNowTick] = useState(Date.now());
  const [slateSelection, setSlateSelection] = useState(null);
  const activeSlate = useMemo(() => chooseShowdownSlate(
    availableSlates, slateSelection?.site === site ? slateSelection : null, nowTick,
  ), [availableSlates, slateSelection, site, nowTick]);
  const slateKey = activeSlate?.key || "none";
  const scope = `sd.v2.${site}.${slateKey}`;
  const runToken = useRef(0);
  const tagKey = useMemo(() => makeTagKey(cfg.capTag), [cfg.capTag]);

  const [numLineups, setNumLineups] = useStickyState("sd.N", 20);
  const [maxSalary, setMaxSalary] = useStickyState(`${scope}.cap`, cfg.cap);
  // Do not force-reset max salary on mount/refresh. The site-specific sticky
  // key restores the saved value automatically.

  const [optBy, setOptBy] = useStickyState("sd.optBy", "proj");
  const [randomness, setRandomness] = useStickyState("sd.rand", 0);
  const [globalMax, setGlobalMax] = useStickyState("sd.gmax", 100);
  const [maxOverlap, setMaxOverlap] = useStickyState("sd.maxOverlap", 5);
  const [lineupPownCap, setLineupPownCap] = useStickyState("sd.lineupPownCap", "");
  const [geoMeanPownCap, setGeoMeanPownCap] = useStickyState("sd.geoMeanPownCap", "");
  const [q, setQ] = useState("");
  const [hideExcluded, setHideExcluded] = useState(false);
  const [minValue, setMinValue] = useStickyState(`${scope}.minValue`, "");

  // sticky sets/maps for constraints
  const [locks, setLocks] = useStickySet(`${scope}.locks`, []);
  const [excls, setExcls] = useStickySet(`${scope}.excls`, []);
  const [boost, setBoost] = useStickyState(`${scope}.boost`, {});
  const [teamBoost, setTeamBoost] = useStickyState(`${scope}.teamBoost`, {});
  const [customProj, setCustomProj] = useStickyState(`${scope}.customProj`, {});

  // Site-ID-only players are included in the pool, hidden by default,
  // and excluded by default until manually allowed.
  const [hideSiteIdOnly, setHideSiteIdOnly] = useStickyState(`${scope}.hideSiteIdOnly.checkboxDefault`, true);
  const [includedUnprojected, setIncludedUnprojected] = useStickySet(`${scope}.includedUnprojected`, []);

  // per-slot exposure maps, keyed by "Player::CPT" or "Player::MVP" or "Player::FLEX"
  const [minPctTag, setMinPctTag] = useStickyState(`${scope}.minPctTag`, {});
  const [maxPctTag, setMaxPctTag] = useStickyState(`${scope}.maxPctTag`, {});

  // team override per slate team
  const [teamMaxPct, setTeamMaxPct] = useStickyState(`${scope}.teamMax`, {});

  // IF→THEN rules
  const [rules, setRules] = useStickyState(`${scope}.rules`, []);

  // Directional build-level team-stack rules, keyed by "TEAM:5-1", "TEAM:4-2", or "3-3".
  const [stackRules, setStackRules] = useStickyState(`${scope}.stackRules`, {});

  // 🔹 Player Groups (NEW)
  const [groups, setGroups] = useStickyState(`${scope}.groups`, []);

  // Advanced rule panels mirror NFL Classic and start collapsed.
  const [advancedOpen, setAdvancedOpen] = useStickyState(`${scope}.advancedOpen`, {
    conditional: false,
    stacks: false,
    groups: false,
    teamMax: false,
  });

  // CPT/MVP vs FLEX table filter
  const [tagFilter, setTagFilter] = useStickyState(`${scope}.tagFilter`, "ALL");

  // exposure tab scope
  const [expScope, setExpScope] = useStickyState(`${scope}.expScope`, "ALL"); // "ALL" | cfg.capTag | "FLEX"

  // right-panel tab
  const [tab, setTab] = useStickyState(`${scope}.rightTab`, "Exposure"); // Exposure | Teams | Stacks

  // builds & results
  const [lineups, setLineups] = useState([]);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [progressUI, setProgressUI] = useState(0);
  const [progressActual, setProgressActual] = useState(0);
  const tickRef = useRef(null);

  // Build history (chips) — persistent per site
  const [builds, setBuilds] = useStickyState(`${scope}.builds`, []); // [{id, at, n, params, lineups}]
  const [activeBuild, setActiveBuild] = useStickyState(`${scope}.activeBuild`, -1);
  const liveBuildRef = useRef(null);
  useEffect(() => {
    const previous = liveBuildRef.current;
    if (previous && previous.scope !== scope && previous.isOptimizing && previous.lineups.length) {
      const rec = { id: Date.now(), at: new Date().toISOString(), n: previous.lineups.length,
        site: previous.site, slateKey: previous.slateKey, slateLabel: previous.label,
        params: previous.params, lineups: previous.lineups };
      const saved = [...previous.builds, rec];
      try {
        localStorage.setItem(`${previous.scope}.builds`, JSON.stringify(saved));
        localStorage.setItem(`${previous.scope}.activeBuild`, JSON.stringify(saved.length - 1));
      } catch {}
    }
    liveBuildRef.current = { scope, site, slateKey, label: activeSlate?.label, isOptimizing, lineups, builds,
      params: { numLineups, optBy, randomness, maxSalary, maxOverlap, lineupPownCap, geoMeanPownCap, stackRules, teamBoost } };
  });

// live clock
useEffect(() => {
  const id = setInterval(() => setNowTick(Date.now()), 1000);
  return () => clearInterval(id);
}, []);

const fmtTime = (d) =>
  d ? d.toLocaleString("en-US", { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

const parseVegasDate = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmtVegasTime = (value) => {
  const raw = String(value || "");
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!match) return "—";
  const d = new Date(Date.UTC(+match[1], +match[2] - 1, +match[3], +match[4], +match[5]));
  return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" }).format(d) + " ET";
};

const fmtSpread = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  if (n === 0) return "PK";
  const body = Number.isInteger(Math.abs(n))
    ? String(Math.abs(n))
    : Math.abs(n).toFixed(1).replace(/\.0$/, "");
  return `${n > 0 ? "+" : "-"}${body}`;
};

// relative age: "Xm" -> "X minutes", then "Y hours"
const rel = (d) => {
  if (!d) return null;
  const secs = Math.max(0, Math.round((nowTick - d.getTime()) / 1000));
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"}`;
  const hrs = Math.floor(mins / 60);
  return `${hrs} hour${hrs === 1 ? "" : "s"}`;
};

  useEffect(() => {
    if (!isOptimizing) return; clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setProgressUI((p) => {
        const N = Math.max(1, Number(numLineups) || 1);
        const target = Math.max(progressActual, 1);
        const ceiling = Math.max(0, N - 1);
        return Math.min(Math.max(p + 1, target), ceiling);
      });
    }, 250);
    return () => clearInterval(tickRef.current);
  }, [isOptimizing, progressActual, numLineups]);

  useEffect(() => () => { runToken.current += 1; }, []);

  // On site switch, clear live results but keep build history (history is per-site via key)
  useEffect(() => {
    setLineups([]); setProgressActual(0); setProgressUI(0); setIsOptimizing(false);
    runToken.current += 1;
  }, [site, slateKey]);

  // If user selects a chip (activeBuild), load its lineups into the live view
  useEffect(() => {
    if (!isOptimizing && activeBuild >= 0 && builds[activeBuild]?.slateKey === slateKey) {
      setLineups(builds[activeBuild].lineups || []);
    }
  }, [activeBuild, builds, isOptimizing, slateKey]);

  /* ------------------------------ rows ------------------------------ */
  const rows = useMemo(() => buildShowdownRows({
    slate: activeSlate, site, simulations: simulationFeed.data,
    projections: data, legacyIds: meta.data, customProj,
  }), [activeSlate, site, simulationFeed.data, data, meta.data, customProj]);
  const ownershipAvailable = rows.length > 0 && rows.some((r) => r.pown != null || r.cap_pown != null);
  const optimalAvailable = rows.length > 0 && rows.some((r) => r.opt != null || r.cap_opt != null);
  useEffect(() => {
    if ((optBy === "pown" && !ownershipAvailable) || (optBy === "opt" && !optimalAvailable)) setOptBy("proj");
  }, [optBy, ownershipAvailable, optimalAvailable]); // eslint-disable-line react-hooks/exhaustive-deps

  /* slate teams (only 2 in showdown) */
  const slateTeams = useMemo(() => {
    const s = new Set(); for (const r of rows) { if (r.team) s.add(r.team); if (r.opp) s.add(r.opp); } return [...s].slice(0, 2);
  }, [rows]);

  const directionalStackRows = useMemo(() => {
    if (slateTeams.length !== 2) return [];
    const [teamA, teamB] = slateTeams;
    return [
      { key: `${teamA}:5-1`, team: teamA, team_count: 5, opp_count: 1, label: `${teamA} 5 – ${teamB} 1` },
      { key: `${teamB}:5-1`, team: teamB, team_count: 5, opp_count: 1, label: `${teamB} 5 – ${teamA} 1` },
      { key: `${teamA}:4-2`, team: teamA, team_count: 4, opp_count: 2, label: `${teamA} 4 – ${teamB} 2` },
      { key: `${teamB}:4-2`, team: teamB, team_count: 4, opp_count: 2, label: `${teamB} 4 – ${teamA} 2` },
      { key: "3-3", team: "ANY", team_count: 3, opp_count: 3, label: `${teamA} 3 – ${teamB} 3` },
    ];
  }, [slateTeams]);

  const showdownGame = useMemo(() => {
    const teams = new Set(slateTeams.map(normTeam).filter(Boolean));

    const vegasRows = Array.isArray(vegasData?.rows)
      ? vegasData.rows
      : Array.isArray(vegasData?.data)
      ? vegasData.data
      : Array.isArray(vegasData)
      ? vegasData
      : [];

    const matching = vegasRows.filter((row) => {
      const team = normTeam(row?.team ?? row?.Team ?? "");
      const opponent = normTeam(
        row?.opponent ?? row?.Opponent ?? row?.opp ?? row?.Opp ?? ""
      );
      return team && opponent && teams.has(team) && teams.has(opponent);
    });

    if (!matching.length) {
      const fallbackAway = slateTeams[0] || "";
      const fallbackHome = slateTeams[1] || "";

      return {
        away: fallbackAway,
        home: fallbackHome,
        gameDate: "",
        total: null,
        awayTotal: null,
        homeTotal: null,
        awaySpread: null,
        homeSpread: null,
      };
    }

    const first = matching[0] || {};
    const field = normTeam(
      first.field ?? first.Field ?? first.home ?? first.Home ?? ""
    );

    const teamA = normTeam(first.team ?? first.Team ?? "");
    const opponentA = normTeam(
      first.opponent ?? first.Opponent ?? first.opp ?? first.Opp ?? ""
    );

    const home =
      field && teams.has(field)
        ? field
        : opponentA || slateTeams[1] || "";

    const away =
      home === teamA
        ? opponentA
        : teamA || slateTeams.find((team) => normTeam(team) !== home) || "";

    const rowByTeam = new Map(
      matching.map((row) => [
        normTeam(row.team ?? row.Team ?? ""),
        row,
      ])
    );

    const awayRow = rowByTeam.get(away) || {};
    const homeRow = rowByTeam.get(home) || {};

    const valueOrNull = (value) =>
      value == null || value === "" || !Number.isFinite(Number(value))
        ? null
        : Number(value);

    return {
      away,
      home,
      gameDate:
        first.gameDate ??
        first.game_date ??
        awayRow.gameDate ??
        homeRow.gameDate ??
        "",
      total: valueOrNull(
        first.over_under ??
          awayRow.over_under ??
          homeRow.over_under
      ),
      awayTotal: valueOrNull(
        awayRow.team_total ?? awayRow.teamTotal
      ),
      homeTotal: valueOrNull(
        homeRow.team_total ?? homeRow.teamTotal
      ),
      awaySpread: valueOrNull(awayRow.spread),
      homeSpread: valueOrNull(homeRow.spread),
    };
  }, [slateTeams, vegasData]);

  /* Unique player names for pickers */
  const allPlayers = useMemo(() => {
    const s = new Set(rows.map(r => r.name));
    return [...s].sort((a,b)=>a.localeCompare(b));
  }, [rows]);

  /* -------------------- table + search + sorting ------------------- */
  const [sort, setSortState] = useState({ col: "proj", dir: "desc" });
  const [sortSnapshot, setSortSnapshot] = useState({ scope: null, values: {} });

  const usagePct = useMemo(() => {
    if (!lineups.length) return {};
    const m = new Map();
    for (const L of lineups) {
      const ps = Array.isArray(L.pairs)
        ? L.pairs
        : (L.players || []).map((n, i) => ({ slot: i === 0 ? cfg.capTag : "FLEX", name: n }));
      for (const p of ps) {
        const t = p.slot === "FLEX" ? "FLEX" : cfg.capTag;
        const k = `${p.name}::${t}`;
        m.set(k, (m.get(k) || 0) + 1);
      }
    }
    const total = Math.max(1, lineups.length);
    const out = {};
    for (const [k, cnt] of m.entries()) out[k] = (cnt / total) * 100;
    return out;
  }, [lineups, cfg.capTag]);

  const expandedRows = useMemo(() => {
    const res = []; for (const r of rows) {
      const multiplier = 1 + 0.03 * num(boost[r.name]) +
        (r.pos !== "DST" ? 0.10 * num(teamBoost[r.team]) : 0);
      res.push({
        ...r,
        tag: cfg.capTag,
        projDisplay: num(r.cap_proj) * multiplier,
        unboostedProjDisplay: r.cap_proj,
        baseProjDisplay: r.base_cap_proj ?? r.cap_proj,
        customProjValue: r.custom_cap_proj ?? "",
        hasCustomProj: !!r.has_custom_cap_proj,
        floorDisplay: r.cap_floor,
        ceilDisplay: r.cap_ceil,
        pownDisplay: r.cap_pown,
        optDisplay: r.cap_opt,
        salaryDisplay: r.cap_salary,
        key: `${r.name}::${cfg.capTag}`
      });
      res.push({
        ...r,
        tag: "FLEX",
        projDisplay: num(r.proj) * multiplier,
        unboostedProjDisplay: r.proj,
        baseProjDisplay: r.base_proj ?? r.proj,
        customProjValue: r.custom_proj ?? "",
        hasCustomProj: !!r.has_custom_proj,
        floorDisplay: r.floor,
        ceilDisplay: r.ceil,
        pownDisplay: r.pown,
        optDisplay: r.opt,
        salaryDisplay: r.salary,
        key: r.name + "::FLEX"
      });
    }
    return res.map((r) => ({
      ...r,
      valueDisplay: num(r.salaryDisplay) > 0
        ? num(r.projDisplay) * 1000 / num(r.salaryDisplay)
        : 0,
    }));
  }, [rows, cfg.capTag, boost, teamBoost]);

  // Capture editable sort values once per slate. Keep existing values during
  // projection/boost edits; newly loaded slots receive their first sort values.
  useEffect(() => {
    setSortSnapshot((prev) => {
      const sameScope = prev.scope === scope;
      const values = sameScope ? { ...prev.values } : {};
      let changed = !sameScope;
      for (const r of expandedRows) {
        if (Object.prototype.hasOwnProperty.call(values, r.key)) continue;
        values[r.key] = { proj: num(r.projDisplay), val: num(r.valueDisplay) };
        changed = true;
      }
      return changed ? { scope, values } : prev;
    });
  }, [expandedRows, scope]);

  const defaultExcludedKeys = useMemo(() => {
    const s = new Set();
    for (const r of expandedRows) {
      if (!r.siteIdOnly) continue;
      if (includedUnprojected.has(r.key)) continue;
      s.add(r.key);
    }
    return s;
  }, [expandedRows, includedUnprojected]);

  // Value is boosted projection per $1,000, evaluated separately for each slot.
  const valueExcludedKeys = useMemo(() => {
    const threshold = Number(minValue);
    if (String(minValue).trim() === "" || !Number.isFinite(threshold)) return new Set();
    return new Set(expandedRows
      .filter((r) => r.valueDisplay < threshold)
      .map((r) => r.key));
  }, [expandedRows, minValue]);

  const effectiveExcls = useMemo(() => {
    const s = new Set(excls);
    for (const k of defaultExcludedKeys) s.add(k);
    for (const k of valueExcludedKeys) s.add(k);
    return s;
  }, [excls, defaultExcludedKeys, valueExcludedKeys]);

  const isDefaultSiteIdOnlyExcluded = (row) => row?.siteIdOnly && defaultExcludedKeys.has(row.key);

  const filteredRows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const okTag = (t) => tagFilter === "ALL" ? true : (t === "FLEX" ? "FLEX" : cfg.capTag) === tagFilter;
    return expandedRows.filter((r) =>
      (!hideSiteIdOnly || !r.siteIdOnly) &&
      (!hideExcluded || !effectiveExcls.has(r.key)) &&
      okTag(r.tag) &&
      (
        !needle ||
        r.name.toLowerCase().includes(needle) ||
        r.team.toLowerCase().includes(needle) ||
        r.opp.toLowerCase().includes(needle) ||
        r.pos.toLowerCase().includes(needle) ||
        String(r.salaryDisplay).includes(needle)
      )
    );
  }, [expandedRows, q, tagFilter, cfg.capTag, hideSiteIdOnly, hideExcluded, effectiveExcls]);

  const cmp = useMemo(() => {
    const { col, dir } = sort; const mult = dir === "asc" ? 1 : -1;
    return (a, b) => {
      if (["tag","pos","team","opp"].includes(col)) {
        const va = (col === "tag" ? (a.tag === "FLEX" ? "FLEX" : cfg.capTag) :
                    col === "pos" ? a.pos :
                    col === "team" ? a.team : a.opp) || "";
        const vb = (col === "tag" ? (b.tag === "FLEX" ? "FLEX" : cfg.capTag) :
                    col === "pos" ? b.pos :
                    col === "team" ? b.team : b.opp) || "";
        if (va < vb) return -1 * mult;
        if (va > vb) return  1 * mult;
        return a.name.localeCompare(b.name) * mult;
      }
      const map = { salary:"salaryDisplay", proj:"projDisplay", val:"valueDisplay", floor:"floorDisplay", ceil:"ceilDisplay", pown:"pownDisplay", opt:"optDisplay", usage:null };
      const sortValue = (r) => {
        if (col === "usage") return usagePct[r.key] || 0;
        if (col === "proj" || col === "val") {
          const saved = sortSnapshot.scope === scope ? sortSnapshot.values[r.key] : null;
          if (saved) return saved[col];
        }
        return num(r[map[col]]);
      };
      const aV = sortValue(a);
      const bV = sortValue(b);
      if (aV < bV) return -1 * mult;
      if (aV > bV) return  1 * mult;
      return a.name.localeCompare(b.name) * mult;
    };
  }, [sort, cfg.capTag, usagePct, sortSnapshot, scope]);

  const sortedRows = useMemo(() => [...filteredRows].sort(cmp), [filteredRows, cmp]);

  const sortable = new Set(["tag","pos","team","opp","salary","proj","val","floor","ceil","pown","opt","usage"]);
  const setSort = (col) => {
    if (!sortable.has(col)) return;
    // An explicit header click sorts using the current edited/boosted values.
    setSortSnapshot({
      scope,
      values: Object.fromEntries(expandedRows.map((r) => [r.key, {
        proj: num(r.projDisplay), val: num(r.valueDisplay),
      }])),
    });
    setSortState((prev) => ({
      col,
      dir: prev.col === col ? (prev.dir === "asc" ? "desc" : "asc") : "desc",
    }));
  };
  const sortArrow = (key) => sort.col === key ? (sort.dir === "asc" ? " ▲" : " ▼") : "";

  /* ------------------------------ actions ---------------------------- */
  const toggleLock = (name, tag) =>
    setLocks((s) => {
      const k = tagKey(name, tag);
      const n = new Set(s);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });

  const toggleExcl = (name, tag) =>
    setExcls((s) => {
      const k = tagKey(name, tag);
      const n = new Set(s);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });

  const toggleExclRow = (row) => {
    const k = tagKey(row.name, row.tag);
    if (valueExcludedKeys.has(k)) return;

    // Site-ID-only rows are excluded by default. Unchecking Excl means
    // explicitly allowing that one row into the optimizer pool.
    if (row.siteIdOnly && defaultExcludedKeys.has(k)) {
      setIncludedUnprojected((prev) => {
        const next = new Set(prev);
        next.add(k);
        return next;
      });
      setExcls((prev) => {
        const next = new Set(prev);
        next.delete(k);
        return next;
      });
      return;
    }

    if (row.siteIdOnly && includedUnprojected.has(k)) {
      setIncludedUnprojected((prev) => {
        const next = new Set(prev);
        next.delete(k);
        return next;
      });
      setExcls((prev) => {
        const next = new Set(prev);
        next.add(k);
        return next;
      });
      return;
    }

    toggleExcl(row.name, row.tag);
  };

  const bumpBoost = (name, step) =>
    setBoost((m) => ({ ...m, [name]: clamp(num(m[name]) + step, -6, 6) }));

  const bumpTeamBoost = (team, step) =>
    setTeamBoost((m) => ({ ...m, [team]: clamp(num(m[team]) + step, -6, 6) }));

  const setPlayerCustomProj = (name, tag, value) => {
    const k = tagKey(name, tag);
    setCustomProj((m) => {
      const next = { ...(m || {}) };
      if (value == null || String(value).trim() === "") delete next[k];
      else next[k] = value;
      return next;
    });
  };

  const resetPlayerCustomProj = (name, tag) => {
    const k = tagKey(name, tag);
    setCustomProj((m) => {
      const next = { ...(m || {}) };
      delete next[k];
      return next;
    });
  };

const resetConstraints = () => {
  runToken.current += 1;
  // player / team / exposure / rules
  setLocks(new Set());
  setExcls(new Set());
  setBoost({});
  setTeamBoost({});
  setCustomProj({});
  setHideSiteIdOnly(true);
  setHideExcluded(false);
  setMinValue("");
  setIncludedUnprojected(new Set());
  setMinPctTag({});
  setMaxPctTag({});
  setRules([]);
  setStackRules({});
  setTeamMaxPct({});
  setGroups([]);
  setAdvancedOpen({ conditional: false, stacks: false, groups: false, teamMax: false });

  // clear results + build history
  setBuilds([]);
  setActiveBuild(-1);
  setLineups([]);
  setProgressUI(0);
  setProgressActual(0);

  // UI filters/search/tabs
  setQ("");
  setTagFilter("ALL");
  setExpScope("ALL");
  setTab("Exposure");

  // top-level numeric controls
  setNumLineups(20);
  setRandomness(0);
  setGlobalMax(100);
  setLineupPownCap("");
  setGeoMeanPownCap("");
  // optionally:
  // setMaxOverlap(5);
};


  // --- Quick Clears (place directly under resetConstraints) ---
  const clearAll = () => {
    // same as resetConstraints
    setLocks(new Set());
    setExcls(new Set());
    setBoost({});
    setTeamBoost({});
    setCustomProj({});
    setHideSiteIdOnly(true);
  setHideExcluded(false);
  setMinValue("");
    setIncludedUnprojected(new Set());
    setMinPctTag({});
    setMaxPctTag({});
    setRules([]);
    setStackRules({});
    setTeamMaxPct({});
    setGroups([]);
    setAdvancedOpen({ conditional: false, stacks: false, groups: false, teamMax: false });
  };

  const clearExcludesAll = () => {
    setExcls(new Set());
    setIncludedUnprojected(new Set());
  };
  const clearLocks       = () => setLocks(new Set());
  const clearBoosts      = () => {
    setBoost({});
    setTeamBoost({});
  };
  const clearMinMax      = () => {
    setMinPctTag({});
    setMaxPctTag({});
  };

  // --- NEW bulk exclude toggles ------------------------------------

  // Toggle: exclude only the current slot filter (ALL / CPT/MVP / FLEX).
  // Re-clicking will undo the exclude for that slot.
  const toggleExcludeCurrentSlot = () => {
    setExcls((prev) => {
      const next = new Set(prev);

      // Determine which slot(s) we are targeting based on tagFilter
      const targets = expandedRows.filter((r) => {
        const slot = r.tag === "FLEX" ? "FLEX" : cfg.capTag;
        if (tagFilter === "ALL") return true;
        return slot === tagFilter;
      });

      if (!targets.length) return next;

      let allExcluded = true;
      for (const r of targets) {
        const k = tagKey(r.name, r.tag);
        if (!next.has(k)) {
          allExcluded = false;
          break;
        }
      }

      if (allExcluded) {
        // All players for this slot are excluded → un-exclude them
        for (const r of targets) next.delete(tagKey(r.name, r.tag));
      } else {
        // Some not excluded → exclude them all
        for (const r of targets) next.add(tagKey(r.name, r.tag));
      }

      return next;
    });
  };

  /* --------------------------- optimize (SSE) ------------------------ */
  async function optimize() {
    if (!rows.length || !activeSlate || isOptimizing || loading || meta.loading || err || meta.err) return;
    const solveToken = ++runToken.current;
    setLineups([]); setProgressActual(0); setProgressUI(0); setIsOptimizing(true);
    const basePos = ["QB","RB","WR","TE","DST","K"];
    const slots = [ { name: cfg.capTag === "CPT" ? "CPT" : "MVP", eligible: basePos }, ...Array.from({length:5},()=>({ name:"FLEX", eligible: basePos })) ];

    const players = rows
      .filter((r) => {
        if (!basePos.includes(r.pos)) return false;
        if (!r.salary || !r.cap_salary) return false;
        const flexKey = `${r.name}::FLEX`;
        const capKey = `${r.name}::${cfg.capTag}`;
        const bothSlotsExcluded = effectiveExcls.has(flexKey) && effectiveExcls.has(capKey);
        return !r.siteIdOnly || !bothSlotsExcluded;
      })
      .map((r) => ({
        name: r.name, pos: r.pos, team: r.team, opp: r.opp,
        salary: Math.round(r.salary || 0), proj: r.proj || 0, floor: r.floor || 0, ceil: r.ceil || 0,
        pown: r.pown || 0, opt: r.opt || 0,
        cap_salary: Math.round(r.cap_salary || Math.round(r.salary * 1.5)),
        cap_proj: r.cap_proj, cap_floor: r.cap_floor, cap_ceil: r.cap_ceil, cap_pown: r.cap_pown, cap_opt: r.cap_opt
      }));

    const apiRules = (rules || []).map((r) => ({
      if_slot: r.if_slot || (cfg.capTag),
      if_pos: r.if_type === "players" ? [] : (r.if_pos || ["QB"]),
      if_players: r.if_type === "players" ? (r.if_players || []) : [],
      if_team_exact: r.if_team ? r.if_team : null,
      then_mode: r.then_mode || "at_least",
      then_at_least: Math.max(0, Number(r.then_at_least) || 0),
      from_pos: r.from_type === "players" ? [] : (r.from_pos || ["WR","TE"]),
      from_players: r.from_type === "players" ? (r.from_players || []) : [],
      team_scope: r.team_scope || "any",
      team_exact: (r.team_scope === "exact_team" && r.team_exact) ? r.team_exact : null
    }));

    // team max overrides (only provided teams)
    const teamMax = {};
    for (const tm of slateTeams) {
      const v = clamp(Number(teamMaxPct[tm]) || 0, 0, 100);
      if (String(teamMaxPct[tm] ?? "").trim() !== "") teamMax[tm] = v;
    }

    const min_pct_tag = Object.fromEntries(
      Object.entries(minPctTag).map(([k,v]) => [k, clamp(Number(v) || 0, 0, 100)])
    );
    const max_pct_tag = Object.fromEntries(
      Object.entries(maxPctTag).map(([k,v]) => [k, clamp(Number(v) || 100, 0, 100)])
    );

    // Match NFL Classic: validate every returned lineup on the frontend too.
    // Geo Mean intentionally uses the shared geoMeanPct helper, which ignores
    // literal 0% values just like Classic's displayed/validated Geo Mean.
    const rowsByNameForCaps = new Map(rows.map((r) => [r.name, r]));

    const lineupPairs = (L) =>
      Array.isArray(L?.pairs) && L.pairs.length
        ? L.pairs
        : (L?.players || []).map((name, i) => ({
            slot: i === 0 ? cfg.capTag : "FLEX",
            name,
          }));

    const lineupPownPct = (L) =>
      lineupPairs(L).reduce((sum, p) => {
        const r = rowsByNameForCaps.get(p.name);
        if (!r) return sum;
        const isCap = String(p.slot || "").toUpperCase() !== "FLEX";
        const own = isCap ? (r.cap_pown ?? r.pown ?? 0) : (r.pown ?? 0);
        return sum + Number(own || 0) * 100;
      }, 0);

    const lineupGeoMeanPownPct = (L) =>
      geoMeanPct(
        lineupPairs(L)
          .map((p) => {
            const r = rowsByNameForCaps.get(p.name);
            if (!r) return null;
            const isCap = String(p.slot || "").toUpperCase() !== "FLEX";
            return {
              pown: Number(isCap ? (r.cap_pown ?? r.pown ?? 0) : (r.pown ?? 0)) * 100,
            };
          })
          .filter(Boolean),
        (x) => x.pown
      );

    const lineupCap =
      !ownershipAvailable || String(lineupPownCap).trim() === ""
        ? null
        : clamp(Number(lineupPownCap) || 0, 0, 1000);

    const geoMeanCap =
      !ownershipAvailable || String(geoMeanPownCap).trim() === ""
        ? null
        : clamp(Number(geoMeanPownCap) || 0, 0, 100);

    const apiStackRules = directionalStackRows
      .map((shape) => {
        const saved = stackRules?.[shape.key] || {};
        const minBlank = String(saved.min ?? "").trim() === "";
        const maxBlank = String(saved.max ?? "").trim() === "";
        return {
          ...shape,
          min_pct: minBlank ? null : clamp(Number(saved.min) || 0, 0, 100),
          max_pct: maxBlank ? null : clamp(Number(saved.max) || 0, 0, 100),
          active: !minBlank || !maxBlank,
        };
      });

    const requestedLineups = Math.max(1, Number(numLineups) || 1);

    const invalidStackRule = apiStackRules.find(
      (r) => r.min_pct != null && r.max_pct != null && r.min_pct > r.max_pct
    );
    if (invalidStackRule) {
      alert(`${invalidStackRule.label}: Min % cannot be greater than Max %.`);
      setIsOptimizing(false);
      clearInterval(tickRef.current);
      return;
    }

    const stackMinCounts = apiStackRules.map((r) =>
      r.min_pct == null ? 0 : Math.ceil((r.min_pct * requestedLineups) / 100 - 1e-12)
    );
    const stackMaxCounts = apiStackRules.map((r) =>
      r.max_pct == null ? requestedLineups : Math.floor((r.max_pct * requestedLineups) / 100 + 1e-12)
    );

    const impossibleRoundedRule = apiStackRules.find(
      (_r, index) => stackMinCounts[index] > stackMaxCounts[index]
    );
    if (impossibleRoundedRule) {
      alert(`${impossibleRoundedRule.label}: those percentages cannot both be met in a ${requestedLineups}-lineup build.`);
      setIsOptimizing(false);
      clearInterval(tickRef.current);
      return;
    }

    if (stackMinCounts.reduce((sum, count) => sum + count, 0) > requestedLineups) {
      alert(`The stack-rule minimums require more than ${requestedLineups} lineups after rounding.`);
      setIsOptimizing(false);
      clearInterval(tickRef.current);
      return;
    }

    if (stackMaxCounts.reduce((sum, count) => sum + count, 0) < requestedLineups) {
      alert("Stack-rule maximums must leave enough room to build 100% of the lineups.");
      setIsOptimizing(false);
      clearInterval(tickRef.current);
      return;
    }

    const payload = {
      site, slots, players,
      n: requestedLineups,
      cap: Math.min(cfg.cap, Number(maxSalary) || cfg.cap),
      objective: optBy === "pown" && !ownershipAvailable || optBy === "opt" && !optimalAvailable ? "proj" : optBy,
      locks: Array.from(locks),
      excludes: Array.from(new Set([
        ...effectiveExcls,
        ...rows.flatMap((r) => {
          const needOwn = ownershipAvailable && (lineupCap != null || geoMeanCap != null || optBy === "pown");
          const needOpt = optimalAvailable && optBy === "opt";
          const missing = [];
          if ((needOwn && r.pown == null) || (needOpt && r.opt == null)) missing.push(`${r.name}::FLEX`);
          if ((needOwn && r.cap_pown == null) || (needOpt && r.cap_opt == null)) missing.push(`${r.name}::${cfg.capTag}`);
          return missing;
        }),
      ])),
      boosts: boost,
      team_boosts: teamBoost,
      randomness: clamp(Number(randomness) || 0, 0, 100),
      global_max_pct: clamp(Number(globalMax) || 100, 0, 100),
      team_max_pct: teamMax,
      min_pct: {},
      max_pct: {},
      min_pct_tag,
      max_pct_tag,
      time_limit_ms: 1500,
      max_overlap: clamp(Number(maxOverlap) || 0, 0, 5), // ignored by backend (safe)
      lineup_pown_max: lineupCap,
      geo_mean_pown_max: geoMeanCap,
      rules: apiRules,
      groups,
      stack_rules: apiStackRules
        .filter((r) => r.active)
        .map(({ team, team_count, opp_count, min_pct, max_pct }) => ({
          team, team_count, opp_count, min_pct, max_pct,
        })),
    };

    const out = [];
    try {
      await solveStreamShowdown(
        payload,
        (evt) => {
          if (solveToken !== runToken.current) return;
          const L = {
            slateKey, site,
            players: evt.drivers || evt.players || [],
            exportRows: rows.filter((r) => (evt.drivers || evt.players || []).includes(r.name)).map((r) => ({ ...r, __raw: undefined })),
            pairs: Array.isArray(evt.pairs)
              ? evt.pairs
              : (evt.drivers || evt.players || []).map((n, i) => ({ slot: i === 0 ? cfg.capTag : "FLEX", name: n })),
            salary: evt.salary,
            total: evt.total,
          };

          // Same safety layer as NFL Classic: the lineup shown to the user must
          // satisfy the exact ownership numbers shown/calculated by the frontend.
          if ((lineupCap != null || geoMeanCap != null || optBy === "pown") && ownershipAvailable && !showdownLineupRates(L, rowsByNameForCaps).ownComplete) return;
          if (lineupCap != null && lineupPownPct(L) > lineupCap + 1e-9) return;
          if (geoMeanCap != null && lineupGeoMeanPownPct(L) > geoMeanCap + 1e-9) return;

          out.push(L);
          setLineups((prev) => [...prev, L]);
          setProgressActual(out.length);
        },
        (doneEvt) => {
          if (solveToken !== runToken.current) return;
          setProgressActual(out.length || payload.n);
          setProgressUI(out.length || payload.n);
          setIsOptimizing(false);
          clearInterval(tickRef.current);

          if (String(doneEvt?.reason || "").startsWith("stack_rule")) {
            alert("The requested stack percentages could not be completed with the other active rules and exposures.");
          }

          // Save build in history and select it
          const rec = {
            id: Date.now(),
            slateKey, site, slateLabel: activeSlate.label,
            at: new Date().toISOString(),
            n: out.length,
            params: { numLineups, optBy, randomness, maxSalary, maxOverlap, lineupPownCap, geoMeanPownCap, stackRules, teamBoost },
            lineups: out
          };
          setBuilds((B) => {
            const next = [...B, rec];
            setActiveBuild(next.length - 1);
            return next;
          });
        }
      );
    } catch (e) {
      if (solveToken !== runToken.current) return;
      alert(`Solve failed: ${String(e?.message || e)}`);
      setIsOptimizing(false); clearInterval(tickRef.current);
    }
  }

  /* --------------------------- table schema ------------------------- */
  const TABLE_COLS = [
    { key: "lock", label: "Lock" },
    { key: "excl", label: "Excl" },
    { key: "boosts", label: "Boosts" },
    { key: "tag", label: cfg.capTag, sortable: true },
    { key: "name", label: "Player" },
    { key: "team", label: "Tm", sortable: true },
    { key: "opp", label: "Opp", sortable: true },
    { key: "pos", label: "Pos", sortable: true },
    { key: "salary", label: "Salary", sortable: true },
    { key: "proj", label: "Proj", sortable: true },
    { key: "val", label: "Val", sortable: true },
    { key: "floor", label: "Floor", sortable: true },
    { key: "ceil", label: "Ceiling", sortable: true },
    { key: "pown", label: "pOWN%", sortable: true },
    { key: "opt", label: "Opt%", sortable: true },
    { key: "min", label: "Min%" },
    { key: "max", label: "Max%" },
    { key: "usage", label: "Usage%", sortable: true }
  ];

  const totalLabel = optBy === "proj" ? "Proj" : optBy === "floor" ? "Floor" : optBy === "ceil" ? "Ceil" : optBy === "pown" ? "pOWN%" : "Opt%";
  const cell = cls.cell; const header = cls.tableHead; const textSz = "text-[12px]";

  /* --------------------- right-panel aggregates --------------------- */
  const slotLabel = (t) => (t === "ALL" ? "ALL" : (t === "FLEX" ? "FLEX" : cfg.capTag));

  const exposures = useMemo(() => {
    const denom = Math.max(1, lineups.length);

    const perSlot = new Map();
    const perAll = new Map();

    for (const L of lineups) {
      const ps = Array.isArray(L.pairs)
        ? L.pairs
        : (L.players || []).map((n, i) => ({ slot: i === 0 ? cfg.capTag : "FLEX", name: n }));

      const seenThisLineup = new Set();

      for (const p of ps) {
        const t = p.slot === "FLEX" ? "FLEX" : cfg.capTag;
        const k = `${p.name}::${t}`;
        perSlot.set(k, (perSlot.get(k) || 0) + 1);

        if (!seenThisLineup.has(p.name)) {
          perAll.set(p.name, (perAll.get(p.name) || 0) + 1);
          seenThisLineup.add(p.name);
        }
      }
    }

    if (expScope === "ALL") {
      return [...perAll.entries()]
        .map(([name, c]) => ({ name, slot: "ALL", count: c, pct: +(100 * c / denom).toFixed(1) }))
        .sort((a, b) => b.pct - a.pct || a.name.localeCompare(b.name));
    }

    const needSlot = expScope === "FLEX" ? "FLEX" : cfg.capTag;
    return [...perSlot.entries()]
      .filter(([key]) => key.endsWith(`::${needSlot}`))
      .map(([key, c]) => {
        const [name, t] = key.split("::");
        return { name, slot: t, count: c, pct: +(100 * c / denom).toFixed(1) };
      })
      .sort((a, b) => b.pct - a.pct || a.name.localeCompare(b.name));
  }, [lineups, cfg.capTag, expScope]);

  const teamExposure = useMemo(() => {
    const cnt = new Map(); let slotsCount = 0;
    for (const L of lineups) {
      const ps = Array.isArray(L.pairs) ? L.pairs : (L.players || []).map((n,i)=>({ slot: i===0 ? cfg.capTag : "FLEX", name:n }));
      for (const p of ps) {
        const meta = rows.find(r => r.name === p.name);
        const tm = meta?.team || "";
        if (!tm) continue;
        cnt.set(tm, (cnt.get(tm) || 0) + 1);
        slotsCount += 1;
      }
    }
    return [...cnt.entries()].map(([tm, c]) => ({ team: tm, count: c, pct: slotsCount ? +(100*c/slotsCount).toFixed(1) : 0 })) .sort((a,b)=>b.pct-a.pct || a.team.localeCompare(b.team));
  }, [lineups, rows]);

  const stackShapes = useMemo(() => {
    const shapes = new Map();
    for (const L of lineups) {
      const names = (Array.isArray(L.players) ? L.players : []).slice();
      const teams = names.map((n)=>rows.find(r=>r.name===n)?.team).filter(Boolean);
      if (!teams.length) continue;
      const counts = new Map();
      for (const team of teams) counts.set(team, (counts.get(team) || 0) + 1);
      const entries = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
      if (entries.length !== 2) continue;
      const [[teamA, countA], [teamB, countB]] = entries;
      const key = `${teamA} ${countA} – ${teamB} ${countB}`;
      shapes.set(key, (shapes.get(key)||0)+1);
    }
    const total = lineups.length || 1;
    return [...shapes.entries()].map(([shape, c]) => ({ shape, count: c, pct: +(100*c/total).toFixed(1) })).sort((a,b)=>b.count-a.count);
  }, [lineups, rows]);

  /* -------- helper: lineup summed pOWN% (players, slot-aware) ------ */
  const lineupPownSumPercent = (L) => {
    const rowsMap = new Map(rows.map((r) => [r.name, r]));
    let sum = 0;
    (L.players || []).forEach((n, i) => {
      const r = rowsMap.get(n);
      if (!r) return;
      sum += ((i === 0 ? r.cap_pown : r.pown) || 0) * 100;
    });
    return sum;
  };

  /* ---------------------------- render ------------------------------ */
  const selectBuild = (idx) => { if (isOptimizing) return; setActiveBuild(idx); if (builds[idx]?.slateKey === slateKey) setLineups(builds[idx].lineups || []); };
  const removeBuild = (idx) => {
    setBuilds((B) => {
      const next = B.slice(0, idx).concat(B.slice(idx + 1));
      const newActive = next.length ? Math.min(idx, next.length - 1) : -1;
      setActiveBuild(newActive);
      setLineups(newActive >= 0 ? (next[newActive].lineups || []) : []);
      return next;
    });
  };
  const clearBuilds = () => { setBuilds([]); setActiveBuild(-1); setLineups([]); };

  return (
    <div className={`${T.page} px-4 md:px-6 py-5`}>
      {/* header row + theme toggle */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-5 gap-y-3 lg:flex-nowrap">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className={`whitespace-nowrap text-xl sm:text-2xl xl:text-3xl font-extrabold ${T.headerText}`}>
            NFL — Showdown Optimizer
          </h1>
          <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] italic sm:flex-nowrap ${T.subtext}`}>
            <span>{`Projections updated: ${projectionsUpdatedAt ? fmtTime(projectionsUpdatedAt) : "—"}`}</span>
            <span>{`Ownership updated: ${ownershipUpdatedAt ? fmtTime(ownershipUpdatedAt) : "—"}`}</span>
          </div>
          <span className={`text-xs ${T.subtext}`}>{`${(rows || []).length.toLocaleString()} rows`}</span>
        </div>

        <div className="flex w-full min-w-0 flex-wrap items-center gap-3 sm:w-auto sm:shrink-0">
          <div className={"inline-flex items-center rounded-full px-1 py-1 text-[11px] ring-1 " + T.pill}>
            <button
              type="button"
              onClick={() => setTheme("dark")}
              className={"px-3 py-1 rounded-full font-medium transition " + (isDark ? T.pillDarkBtnActive : T.pillDarkBtnIdle)}
            >
              Dark
            </button>
            <button
              type="button"
              onClick={() => setTheme("light")}
              className={"px-3 py-1 rounded-full font-medium transition " + (!isDark ? T.pillLightBtnActive : T.pillLightBtnIdle)}
            >
              Light
            </button>
          </div>
            <div className="flex shrink-0 gap-2 items-center">
              <span className="text-xs text-gray-500 whitespace-nowrap">
                Click here if new slate →
              </span>

              <button
                className="h-8 px-4 rounded-md bg-red-600 text-white
                          hover:bg-red-700 active:bg-red-800
                          focus:outline-none focus:ring-2 focus:ring-red-500/50"
                onClick={resetConstraints}
              >
                Reset
              </button>
            </div>
        </div>
      </div>
      <section aria-label="Slate and site" className={`${cls.card} ${T.card} p-3 mb-3`}>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 lg:flex-nowrap">
        <div role="tablist" aria-label="Fantasy site"
          className={"inline-flex shrink-0 items-center gap-6 border-b " +
            (isDark ? "border-slate-800" : "border-slate-200")}
        >
          {Object.keys(SITES).map((s) => {
            const active = site === s;

            return (
              <button
                key={s}
                type="button"
                disabled={isOptimizing}
                onClick={() => setSite(s)}
                role="tab"
                aria-selected={active}
                className={
                  "relative inline-flex h-9 items-center gap-2 whitespace-nowrap pb-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50 " +
                  (active
                    ? (isDark ? "text-white" : "text-slate-950") + " after:absolute after:bottom-[-1px] after:left-0 after:right-0 after:h-0.5 after:rounded-full after:bg-sky-400"
                    : isDark ? "text-slate-400 hover:text-slate-100" : "text-slate-500 hover:text-slate-900")
                }
              >
                <img
                  src={SITES[s].logo}
                  alt=""
                  className="h-4 w-4 object-contain"
                />

                <span>{SITES[s].label}</span>
              </button>
            );
          })}
        </div>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3 sm:flex-nowrap">
        <label htmlFor="showdown-slate" className={T.subtext + " shrink-0 text-xs font-medium"}>Slate</label>
        <select id="showdown-slate" className={inputClass + " min-w-0 max-w-full flex-1 sm:max-w-md"}
          value={slateKey} disabled={isOptimizing || meta.loading || !availableSlates.length}
          onChange={(e) => setSlateSelection({ site, key: e.target.value, selectedAt: Date.now() })}>
          {!availableSlates.length && <option value="none">{meta.loading ? "Loading slates…" : "No showdown slates available"}</option>}
          {availableSlates.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>
        </div>
      <div className="flex flex-wrap gap-3">
        {meta.err && <span className="text-sm text-red-400">Could not load salary/ID data: {String(meta.err.message)}</span>}
        {err && <span className="text-sm text-red-400">Could not load shared simulations: {String(err.message)}</span>}
        {activeSlate && !ownershipAvailable && <span className={T.subtext + " text-xs"}>Showdown ownership unavailable for this slate.</span>}
      </div>
      </section>

        {/* Build settings */}
        <div className={`${cls.card} ${T.card} p-3 mb-3 `}>

          {/* Aligned settings inputs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-3 items-end">
            <div className="min-w-0">
              <label className={"block text-[11px] mb-1 " + (isDark ? "text-slate-300" : "text-gray-600")}>
                Optimize by
              </label>
              <select
                className={inputClass + " w-full min-w-0"}
                value={optBy}
                onChange={(e) => setOptBy(e.target.value)}
              >
                <option value="proj">Projection</option>
                <option value="floor">Floor</option>
                <option value="ceil">Ceiling</option>
                <option disabled={!ownershipAvailable} value="pown">pOWN%</option>
                <option disabled={!optimalAvailable} value="opt">Opt%</option>
              </select>
            </div>

            <div className="min-w-0">
              <label className={"block text-[11px] mb-1 " + (isDark ? "text-slate-300" : "text-gray-600")}>
                Lineups
              </label>
              <input
                className={inputClass + " w-full min-w-0"}
                value={numLineups}
                onChange={(e) => setNumLineups(e.target.value)}
                inputMode="numeric"
              />
            </div>

            <div className="min-w-0">
              <label className={"block text-[11px] mb-1 " + (isDark ? "text-slate-300" : "text-gray-600")}>
                Max salary
              </label>
              <input
                className={inputClass + " w-full min-w-0"}
                value={maxSalary}
                onChange={(e) => setMaxSalary(e.target.value)}
                inputMode="numeric"
              />
            </div>

            <div className="min-w-0">
              <label className={"block text-[11px] mb-1 " + (isDark ? "text-slate-300" : "text-gray-600")}>
                Max Overlap
              </label>
              <input
                className={inputClass + " w-full min-w-0"}
                value={maxOverlap}
                onChange={(e) => setMaxOverlap(e.target.value)}
                inputMode="numeric"
              />
            </div>

            <div className="min-w-0">
              <label className={"block text-[11px] mb-1 " + (isDark ? "text-slate-300" : "text-gray-600")}>
                Randomness %
              </label>
              <input
                className={inputClass + " w-full min-w-0"}
                value={randomness}
                onChange={(e) => setRandomness(e.target.value)}
                inputMode="numeric"
              />
            </div>

            <div className="min-w-0">
              <label className={"block text-[11px] mb-1 " + (isDark ? "text-slate-300" : "text-gray-600")}>
                Max exposure %
              </label>
              <input
                className={inputClass + " w-full min-w-0"}
                value={globalMax}
                onChange={(e) => setGlobalMax(e.target.value)}
                inputMode="numeric"
              />
            </div>

            <div className="min-w-0">
              <label className={"block text-[11px] mb-1 " + (isDark ? "text-slate-300" : "text-gray-600")}>
                Max lineup pOWN% (sum)
              </label>
              <input
                className={inputClass + " w-full min-w-0"}
                placeholder="—"
                disabled={!ownershipAvailable}
                value={lineupPownCap}
                onChange={(e) => setLineupPownCap(e.target.value)}
                inputMode="numeric"
              />
            </div>

            <div className="min-w-0">
              <label className={"block text-[11px] mb-1 " + (isDark ? "text-slate-300" : "text-gray-600")}>
                Geo Mean pOWN%
              </label>
              <input
                className={inputClass + " w-full min-w-0"}
                placeholder="—"
                disabled={!ownershipAvailable}
                value={geoMeanPownCap}
                onChange={(e) => setGeoMeanPownCap(e.target.value)}
                inputMode="numeric"
              />
            </div>

          </div>
        </div>

      {/* Keep advanced build rules together; collapsed by default. */}
      <details className={`${cls.card} ${T.card} p-3 mb-3`}>
        <summary className="cursor-pointer select-none text-sm font-semibold">
          Build Rules
        </summary>
        <div className="mt-3">
      {/* IF → THEN rules */}
      <details
        className={`${cls.card} ${T.card} p-3 mb-3`}
        open={!!advancedOpen.conditional}
        onToggle={(e) => {
          const open = e.currentTarget.open;
          setAdvancedOpen((v) => ({ ...v, conditional: open }));
        }}
      >
        <summary className="cursor-pointer select-none text-sm font-semibold">
          Conditional Rules
          <span className="ml-2 text-[11px] font-normal text-slate-400">IF → THEN</span>
          {rules.length > 0 && (
            <span className="ml-2 rounded-full bg-sky-500/20 px-2 py-0.5 text-[11px] font-medium text-sky-400">
              {rules.length}
            </span>
          )}
        </summary>

      <div className="mt-3">
        <div className="flex items-center justify-between mb-2">
          <div className={isDark ? "text-[11px] text-slate-400" : "text-[11px] text-gray-600"}>
            IF → THEN constraints applied during build
          </div>

          <button
            className={ghostBtnClass}
            onClick={() =>
              setRules((R) => [
                ...R,
                {
                  if_slot: cfg.capTag,
                  if_type: "positions",
                  if_pos: ["QB"],
                  if_players: [],
                  if_team: "",
                  then_mode: "at_least",
                  then_at_least: 1,
                  from_type: "positions",
                  from_pos: ["WR", "TE"],
                  from_players: [],
                  team_scope: "same_team",
                  team_exact: "",
                },
              ])
            }
          >
            + Add Rule
          </button>
        </div>

        {rules.length === 0 ? (
          <div className={isDark ? "text-xs text-slate-500 px-1 py-2" : "text-xs text-gray-500 px-1 py-2"}>
            No rules yet.
          </div>
        ) : (
          <div className="space-y-2">
            {rules.map((r, i) => {
              const destSelectValue =
                r.team_scope === "exact_team" && r.team_exact ? `exact:${r.team_exact}` : r.team_scope || "any";
              const ifTeamValue = r.if_team || "";

              const rowClass = isDark
                ? "relative rounded-lg border border-slate-800 bg-slate-950/40 p-2 pl-3"
                : "relative rounded-lg border border-gray-200 bg-white p-2 pl-3";

              const ifText = isDark ? "text-sm text-slate-200" : "text-sm text-gray-800";
              const pillWrap = isDark
                ? "inline-flex rounded-md overflow-hidden border border-slate-800 bg-slate-950/30"
                : "inline-flex rounded-md overflow-hidden border border-gray-200 bg-white";

              const slotBtn = (active) =>
                isDark
                  ? `px-2 py-1 text-sm transition ${
                      active ? "bg-sky-500 text-slate-950" : "bg-transparent text-slate-200 hover:bg-slate-800/60"
                    }`
                  : `px-2 py-1 text-sm ${active ? "bg-blue-600 text-white" : "bg-white"}`;

              const posBtn = (active) =>
                isDark
                  ? `px-2 py-1 rounded border text-sm transition ${
                      active
                        ? "bg-sky-500 text-slate-950 border-sky-500"
                        : "bg-slate-950/20 text-slate-200 border-slate-700 hover:bg-slate-800/50"
                    }`
                  : `px-2 py-1 rounded border text-sm ${
                      active ? "bg-blue-600 text-white border-blue-600" : "bg-white border-gray-200"
                    }`;

              const fromBtn = (active) =>
                isDark
                  ? `px-2 py-1 rounded border text-sm transition ${
                      active
                        ? "bg-emerald-500 text-slate-950 border-emerald-500"
                        : "bg-slate-950/20 text-slate-200 border-slate-700 hover:bg-slate-800/50"
                    }`
                  : `px-2 py-1 rounded border text-sm ${
                      active ? "bg-green-600 text-white border-green-600" : "bg-white border-gray-200"
                    }`;

              const selectClass =
  `h-8 w-auto px-2 py-1 text-sm rounded-md border outline-none focus:ring-2 focus:ring-sky-500/30 ` +
  (isDark
    ? "bg-slate-950/60 border-slate-700 text-slate-100"
    : "bg-white border-gray-200 text-slate-800") +
  " shrink-0 min-w-[160px]";
              const numInputClass = `${inputClass} ${isDark ? "bg-slate-950/60 border-slate-700 text-slate-100" : ""}`;

              return (
                <div key={i} className={rowClass}>
                  <span className="absolute inset-y-0 left-0 w-1 rounded-l-lg bg-sky-500" />

                  <div className="flex flex-wrap items-center gap-2">
                    <span className={ifText}>IF</span>

                    <div className={pillWrap}>
                      {[cfg.capTag, "FLEX"].map((slot) => (
                        <button
                          key={slot}
                          className={slotBtn((r.if_slot || cfg.capTag) === slot)}
                          onClick={() =>
                            setRules((R) => {
                              const c = [...R];
                              c[i] = { ...c[i], if_slot: slot };
                              return c;
                            })
                          }
                        >
                          {slot}
                        </button>
                      ))}
                    </div>

                    <span className={ifText}>is</span>

                    <select
                      className={selectClass}
                      value={r.if_type || "positions"}
                      onChange={(e) => setRules((R) => {
                        const c = [...R];
                        c[i] = { ...c[i], if_type: e.target.value };
                        return c;
                      })}
                    >
                      <option value="positions">positions</option>
                      <option value="players">specific players</option>
                    </select>

                    {(r.if_type || "positions") === "positions" &&
                      <button
                        className={posBtn(["QB", "RB", "WR", "TE", "DST", "K"].every((p) => r.if_pos?.includes(p)))}
                        onClick={() => setRules((R) => {
                          const c = [...R];
                          const positions = ["QB", "RB", "WR", "TE", "DST", "K"];
                          const allSelected = positions.every((p) => c[i].if_pos?.includes(p));
                          c[i] = { ...c[i], if_pos: allSelected ? [] : positions };
                          return c;
                        })}
                      >
                        ANY
                      </button>}

                    {(r.if_type || "positions") === "positions" &&
                      ["QB", "RB", "WR", "TE", "DST", "K"].map((p) => (
                        <button
                          key={p}
                          className={posBtn(r.if_pos?.includes(p))}
                          onClick={() =>
                            setRules((R) => {
                              const copy = [...R];
                              const cur = new Set(copy[i].if_pos || []);
                              cur.has(p) ? cur.delete(p) : cur.add(p);
                              copy[i] = { ...copy[i], if_pos: [...cur] };
                              return copy;
                            })
                          }
                        >
                          {p}
                        </button>
                      ))}

                    <span className={ifText}>on team</span>

                    <select
                      className={selectClass}
                      value={ifTeamValue}
                      onChange={(e) =>
                        setRules((R) => {
                          const c = [...R];
                          c[i] = { ...c[i], if_team: e.target.value };
                          return c;
                        })
                      }
                    >
                      <option value="">any team</option>
                      {slateTeams.map((abbr) => {
                        const label = `${abbr} — ${(NFL_TEAMS[abbr]?.city ?? abbr)} ${NFL_TEAMS[abbr]?.nickname ?? ""}`.trim();
                        return (
                          <option key={abbr} value={abbr}>
                            {label}
                          </option>
                        );
                      })}
                    </select>

                    <span className={ifText}>THEN require</span>

                    <select
                      className={selectClass}
                      value={r.then_mode || "at_least"}
                      onChange={(e) => setRules((R) => {
                        const c = [...R];
                        c[i] = { ...c[i], then_mode: e.target.value };
                        return c;
                      })}
                    >
                      <option value="at_least">at least</option>
                      <option value="at_most">at most</option>
                      <option value="exactly">exactly</option>
                    </select>

                    <input
                      className={numInputClass}
                      style={{ width: 56 }}
                      value={r.then_at_least ?? 1}
                      onChange={(e) =>
                        setRules((R) => {
                          const c = [...R];
                          c[i] = { ...c[i], then_at_least: e.target.value };
                          return c;
                        })
                      }
                    />

                    <span className={ifText}>from</span>

                    <select
                      className={selectClass}
                      value={r.from_type || "positions"}
                      onChange={(e) => setRules((R) => {
                        const c = [...R];
                        c[i] = { ...c[i], from_type: e.target.value };
                        return c;
                      })}
                    >
                      <option value="positions">positions</option>
                      <option value="players">specific players</option>
                    </select>

                    {(r.from_type || "positions") === "positions" &&
                      <button
                        className={fromBtn(["QB", "RB", "WR", "TE", "DST", "K"].every((p) => r.from_pos?.includes(p)))}
                        onClick={() => setRules((R) => {
                          const c = [...R];
                          const positions = ["QB", "RB", "WR", "TE", "DST", "K"];
                          const allSelected = positions.every((p) => c[i].from_pos?.includes(p));
                          c[i] = { ...c[i], from_pos: allSelected ? [] : positions };
                          return c;
                        })}
                      >
                        ANY
                      </button>}

                    {(r.from_type || "positions") === "positions" &&
                      ["QB", "RB", "WR", "TE", "DST", "K"].map((p) => (
                        <button
                          key={p}
                          className={fromBtn(r.from_pos?.includes(p))}
                          onClick={() =>
                            setRules((R) => {
                              const c = [...R];
                              const cur = new Set(c[i].from_pos || []);
                              cur.has(p) ? cur.delete(p) : cur.add(p);
                              c[i] = { ...c[i], from_pos: [...cur] };
                              return c;
                            })
                          }
                        >
                          {p}
                        </button>
                      ))}

                    <select
                      className={selectClass}
                      value={destSelectValue}
                      onChange={(e) => {
                        const val = e.target.value;
                        setRules((R) => {
                          const c = [...R];
                          if (val.startsWith("exact:")) {
                            const abbr = val.split(":")[1] || "";
                            c[i] = { ...c[i], team_scope: "exact_team", team_exact: abbr };
                          } else {
                            c[i] = { ...c[i], team_scope: val, team_exact: "" };
                          }
                          return c;
                        });
                      }}
                    >
                      <option value="same_team">same team</option>
                      <option value="opp_team">opp team</option>
                      <option value="any">any</option>
                      {slateTeams.map((abbr) => {
                        const label = `${abbr} — ${(NFL_TEAMS[abbr]?.city ?? abbr)} ${NFL_TEAMS[abbr]?.nickname ?? ""}`.trim();
                        return (
                          <option key={abbr} value={`exact:${abbr}`}>
                            {label}
                          </option>
                        );
                      })}
                    </select>

                    {((r.if_type || "positions") === "players" || (r.from_type || "positions") === "players") && (
                      <div className="basis-full grid grid-cols-1 lg:grid-cols-2 gap-2 pt-1">
                        {(r.if_type || "positions") === "players" && (
                          <div>
                            <div className={isDark ? "text-[11px] text-slate-400 mb-1" : "text-[11px] text-gray-600 mb-1"}>
                              IF any selected player is in the chosen slot
                            </div>
                            <GroupPicker
                              allPlayers={allPlayers}
                              selected={r.if_players || []}
                              inputClass={inputClass}
                              isDark={isDark}
                              onToggle={(nm) => setRules((R) => {
                                const c = [...R];
                                const cur = new Set(c[i].if_players || []);
                                cur.has(nm) ? cur.delete(nm) : cur.add(nm);
                                c[i] = { ...c[i], if_players: [...cur] };
                                return c;
                              })}
                            />
                          </div>
                        )}
                        {(r.from_type || "positions") === "players" && (
                          <div>
                            <div className={isDark ? "text-[11px] text-slate-400 mb-1" : "text-[11px] text-gray-600 mb-1"}>
                              THEN count these selected players
                            </div>
                            <GroupPicker
                              allPlayers={allPlayers}
                              selected={r.from_players || []}
                              inputClass={inputClass}
                              isDark={isDark}
                              onToggle={(nm) => setRules((R) => {
                                const c = [...R];
                                const cur = new Set(c[i].from_players || []);
                                cur.has(nm) ? cur.delete(nm) : cur.add(nm);
                                c[i] = { ...c[i], from_players: [...cur] };
                                return c;
                              })}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    <button
                      className={`${ghostBtnClass} ml-auto`}
                      onClick={() => setRules((R) => R.filter((_, j) => j !== i))}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      </details>

      {/* Directional team-stack percentages across the complete build */}
      <details
        className={`${cls.card} ${T.card} p-3 mb-3`}
        open={!!advancedOpen.stacks}
        onToggle={(e) => {
          const open = e.currentTarget.open;
          setAdvancedOpen((v) => ({ ...v, stacks: open }));
        }}
      >
        <summary className="cursor-pointer select-none text-sm font-semibold">
          Stack Rules
          <span className="ml-2 text-[11px] font-normal text-slate-400">Team splits</span>
          {Object.values(stackRules || {}).filter((value) =>
            String(value?.min ?? "").trim() !== "" || String(value?.max ?? "").trim() !== ""
          ).length > 0 && (
            <span className="ml-2 rounded-full bg-cyan-500/20 px-2 py-0.5 text-[11px] font-medium text-cyan-400">
              {Object.values(stackRules || {}).filter((value) =>
                String(value?.min ?? "").trim() !== "" || String(value?.max ?? "").trim() !== ""
              ).length}
            </span>
          )}
        </summary>

      <div className="mt-3">
        <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
          <div className={`text-[11px] ${isDark ? "text-slate-400" : "text-gray-600"}`}>
            Set how often each directional team split can appear across this build. Blank means unrestricted.
          </div>
          <button className={ghostBtnClass} onClick={() => setStackRules({})}>
            Clear Stack Rules
          </button>
        </div>

        {directionalStackRows.length === 5 ? (
          <div className="overflow-x-auto">
            <div className="min-w-[430px]">
              <div className={`grid grid-cols-[minmax(190px,1fr)_110px_110px] gap-2 px-3 pb-1 text-[10px] font-semibold uppercase tracking-wide ${
                isDark ? "text-slate-500" : "text-gray-500"
              }`}>
                <div>Team split</div>
                <div className="text-center">Min %</div>
                <div className="text-center">Max %</div>
              </div>

              <div className="space-y-2">
                {directionalStackRows.map((shape) => {
                  const value = stackRules?.[shape.key] || {};
                  return (
                    <div
                      key={shape.key}
                      className={`relative grid grid-cols-[minmax(190px,1fr)_110px_110px] items-center gap-2 rounded-lg border p-2 pl-4 ${
                        isDark ? "border-slate-700 bg-slate-950/40" : "border-gray-200 bg-white"
                      }`}
                    >
                      <span className="absolute inset-y-0 left-0 w-1 rounded-l-lg bg-cyan-500" />
                      <div className={`text-sm font-semibold ${isDark ? "text-slate-200" : "text-slate-800"}`}>
                        {shape.label}
                      </div>
                      {[
                        ["min", "Minimum percentage"],
                        ["max", "Maximum percentage"],
                      ].map(([field, ariaLabel]) => (
                        <div key={field} className="relative">
                          <input
                            className={`${inputClass} w-full pr-7 text-center`}
                            type="number"
                            min="0"
                            max="100"
                            step="1"
                            placeholder="—"
                            aria-label={`${shape.label} ${ariaLabel}`}
                            value={value[field] ?? ""}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              setStackRules((current) => ({
                                ...(current || {}),
                                [shape.key]: {
                                  ...((current || {})[shape.key] || {}),
                                  [field]: nextValue,
                                },
                              }));
                            }}
                          />
                          <span className={`pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs ${
                            isDark ? "text-slate-500" : "text-gray-400"
                          }`}>
                            %
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
            Stack rules will appear when both teams are available.
          </div>
        )}
      </div>
      </details>

{/* 🔹 Player Groups (at least / at most / exactly N of selected) */}
<details
  className={`${cls.card} ${T.card} p-3 mb-3`}
  open={!!advancedOpen.groups}
  onToggle={(e) => {
    const open = e.currentTarget.open;
    setAdvancedOpen((v) => ({ ...v, groups: open }));
  }}
>
  <summary className="cursor-pointer select-none text-sm font-semibold">
    Player Groups
    <span className="ml-2 text-[11px] font-normal text-slate-400">Lineup groups</span>
    {groups.length > 0 && (
      <span className="ml-2 rounded-full bg-purple-500/20 px-2 py-0.5 text-[11px] font-medium text-purple-400">
        {groups.length}
      </span>
    )}
  </summary>

<div className="mt-3">
  <div className="flex items-center justify-between mb-2">
    <div className={`text-[11px] ${isDark ? "text-slate-400" : "text-gray-600"}`}>
      Create groups like “at least 2 of these players”, “at most 1”, or “exactly 2”.
    </div>

    <button
      className={ghostBtnClass}
      onClick={() => setGroups((G) => [...G, { mode: "at_least", count: 1, players: [] }])}
    >
      + Add Group
    </button>
  </div>

  {groups.length === 0 ? (
    <div className={`text-xs px-1 py-2 ${isDark ? "text-slate-400" : "text-gray-500"}`}>
      No groups yet.
    </div>
  ) : (
    <div className="space-y-2">
      {groups.map((g, i) => (
        <div
          key={i}
          className={`relative rounded-lg border p-2 pl-3 ${
            isDark ? "border-slate-700 bg-slate-950/40" : "border-gray-200 bg-white"
          }`}
        >
          <span className="absolute inset-y-0 left-0 w-1 rounded-l-lg bg-purple-500" />

          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-sm ${isDark ? "text-slate-200" : "text-slate-800"}`}>
              Require
            </span>

            <select
              className={inputClass}
              value={g.mode}
              onChange={(e) =>
                setGroups((G) => {
                  const c = [...G];
                  c[i] = { ...c[i], mode: e.target.value };
                  return c;
                })
              }
            >
              <option value="at_least">at least</option>
              <option value="at_most">at most</option>
              <option value="exactly">exactly</option>
            </select>

            <input
              className={inputClass}
              style={{ width: 64 }}
              value={g.count}
              onChange={(e) =>
                setGroups((G) => {
                  const c = [...G];
                  const v = Math.max(0, Math.floor(Number(e.target.value) || 0));
                  c[i] = { ...c[i], count: v };
                  return c;
                })
              }
            />

            <span className={`text-sm ${isDark ? "text-slate-200" : "text-slate-800"}`}>
              from
            </span>

            {/* Selected chips */}
            <div className="flex flex-wrap gap-1">
              {(g.players || []).map((nm) => (
                <span
                  key={nm}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${
                    isDark
                      ? "border-slate-700 bg-slate-900/60 text-slate-100"
                      : "border-gray-200 bg-gray-50 text-slate-800"
                  }`}
                >
                  {nm}
                  <button
                    className={`${isDark ? "text-slate-400 hover:text-rose-300" : "text-gray-500 hover:text-red-600"}`}
                    title="Remove"
                    onClick={() =>
                      setGroups((G) => {
                        const c = [...G];
                        c[i] = { ...c[i], players: (c[i].players || []).filter((x) => x !== nm) };
                        return c;
                      })
                    }
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>

            {/* Picker */}
            <div className="w-full mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
              <GroupPicker
                allPlayers={allPlayers}   // IMPORTANT: keep this as allPlayers (not "players")
                selected={g.players || []}
                inputClass={inputClass}
                isDark={isDark}
                onToggle={(nm) =>
                  setGroups((G) => {
                    const c = [...G];
                    const cur = new Set(c[i].players || []);
                    if (cur.has(nm)) cur.delete(nm);
                    else cur.add(nm);
                    c[i] = { ...c[i], players: [...cur] };
                    return c;
                  })
                }
              />

              <div className="flex items-center justify-end gap-2">
                <button
                  className={ghostBtnClass}
                  onClick={() => setGroups((G) => G.filter((_, idx) => idx !== i))}
                >
                  Delete Group
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )}
</div>
</details>

      {/* Team Max% Overrides — only slate teams */}
      <details
        className={`${cls.card} ${T.card} p-3 mb-3`}
        open={!!advancedOpen.teamMax}
        onToggle={(e) => {
          const open = e.currentTarget.open;
          setAdvancedOpen((v) => ({ ...v, teamMax: open }));
        }}
      >
        <summary className="cursor-pointer select-none text-sm font-semibold">
          Team Max% Overrides
          {slateTeams.filter((abbr) => String(teamMaxPct[abbr] ?? "").trim() !== "").length > 0 && (
            <span className="ml-2 rounded-full bg-emerald-500/20 px-2 py-0.5 text-[11px] font-medium text-emerald-400">
              {slateTeams.filter((abbr) => String(teamMaxPct[abbr] ?? "").trim() !== "").length}
            </span>
          )}
        </summary>
      <div className="mt-3">
        <div className="text-[11px] text-gray-600 mb-2">Blank = use Max exposure %</div>
        <div className="flex flex-wrap gap-3">
          {slateTeams.map((abbr) => (
            <div key={abbr} className="flex items-center gap-2">
              <TeamPill abbr={abbr} />
              <input
                className={`${inputClass} h-7 py-1 px-2`}
                style={{ width: 64 }}
                placeholder="—"
                value={teamMaxPct[abbr] ?? ""}
                onChange={(e) => setTeamMaxPct((m) => ({ ...m, [abbr]: e.target.value }))}
              />
            </div>
          ))}
        </div>
      </div>
      </details>

        </div>
      </details>

      {/* Build chips */}
      {builds.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {builds.map((b, idx) => {
            const active = idx === activeBuild;
            return (
              <div key={b.id} className={`inline-flex items-center gap-2 border ${active ? "bg-blue-600 text-white border-blue-600" : isDark ? "bg-transparent text-slate-200 border-slate-700 hover:bg-slate-800/50" : "bg-transparent text-slate-800 border-gray-300 hover:bg-gray-100/50"} ${cls.btn.chip}`}>
                <button onClick={() => selectBuild(idx)} className="inline-flex items-center gap-2">
                  <span className="font-semibold">Build {idx + 1}</span>
                  <span className="opacity-80">• {b.n} LUs</span>
                  <span className="opacity-60">{timeAgo(b.at)}</span>
                </button>
                <button onClick={() => removeBuild(idx)} className="ml-1 text-xs opacity-90 hover:opacity-100" title="Delete build">✕</button>
              </div>
            );
          })}
          <button className={ghostBtnClass} onClick={clearBuilds}>Clear</button>
        </div>
      )}

      {/* === Results Layout: Player Pool, Lineups, Exposure, Cards === */}

      {showdownGame.away && showdownGame.home ? (
        <section className={`${T.card} ${cls.card} p-3 mb-3`} aria-label="Games">
          <h2 className="text-sm font-semibold mb-3">Games</h2>
          <div className="overflow-x-auto pb-1">
          <div className="flex min-w-max gap-2.5">
            <div
              className={`w-[168px] shrink-0 overflow-hidden rounded-xl border ${
                isDark
                  ? "border-slate-800 bg-slate-900/90"
                  : "border-slate-200 bg-white"
              }`}
            >
              <div
                className={`border-b px-2 py-1 ${
                  isDark
                    ? "border-slate-800 bg-slate-800/40"
                    : "border-slate-200 bg-slate-50"
                }`}
              >
                <div
                  className={`text-center text-[11px] font-semibold leading-tight ${
                    isDark ? "text-slate-300" : "text-slate-600"
                  }`}
                >
                  <div>{fmtVegasTime(activeSlate?.gameDate || showdownGame.gameDate)}</div>
                  <div className={isDark ? "text-slate-500" : "text-slate-500"}>
                    O/U{" "}
                    {Number.isFinite(showdownGame.total)
                      ? fmt1(showdownGame.total)
                      : "—"}
                  </div>
                </div>
              </div>

              <div className="space-y-1.5 p-2">
                {[
                  {
                    team: showdownGame.away,
                    spread: showdownGame.awaySpread,
                    total: showdownGame.awayTotal,
                  },
                  {
                    team: showdownGame.home,
                    spread: showdownGame.homeSpread,
                    total: showdownGame.homeTotal,
                  },
                ].map((teamRow) => (
                  <div
                    key={teamRow.team}
                    className={`grid grid-cols-[1fr_auto] items-center gap-1.5 rounded-lg px-2 py-1.5 ${
                      isDark ? "bg-slate-800/45" : "bg-slate-50"
                    }`}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <TeamLogo
                        abbr={teamRow.team}
                        className="h-[18px] w-[18px] shrink-0 object-contain"
                      />

                      <span
                        className={`text-[11px] font-semibold ${
                          isDark ? "text-slate-100" : "text-slate-900"
                        }`}
                      >
                        {teamRow.team}
                      </span>

                      <span
                        className={`text-[9px] font-semibold tabular-nums ${
                          isDark ? "text-slate-500" : "text-slate-500"
                        }`}
                      >
                        {fmtSpread(teamRow.spread)}
                      </span>
                    </div>

                    <div className="ml-auto flex items-center gap-1.5 shrink-0">
                      <div className="flex flex-col items-center justify-center leading-none -space-y-[1px]">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            bumpTeamBoost(teamRow.team, +1);
                          }}
                          className={`h-3.5 w-3.5 flex items-center justify-center text-[8px] leading-none rounded-sm transition ${
                            (teamBoost[teamRow.team] || 0) > 0
                              ? isDark
                                ? "text-emerald-300 bg-emerald-500/10"
                                : "text-emerald-700 bg-emerald-50"
                              : isDark
                              ? "text-slate-500 hover:text-slate-200 hover:bg-slate-700/40"
                              : "text-slate-400 hover:text-slate-700 hover:bg-slate-200/70"
                          }`}
                          title={`Boost ${teamRow.team} offense +10%`}
                        >
                          ▲
                        </button>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            bumpTeamBoost(teamRow.team, -1);
                          }}
                          className={`h-3.5 w-3.5 flex items-center justify-center text-[8px] leading-none rounded-sm transition ${
                            (teamBoost[teamRow.team] || 0) < 0
                              ? isDark
                                ? "text-rose-300 bg-rose-500/10"
                                : "text-rose-700 bg-rose-50"
                              : isDark
                              ? "text-slate-500 hover:text-slate-200 hover:bg-slate-700/40"
                              : "text-slate-400 hover:text-slate-700 hover:bg-slate-200/70"
                          }`}
                          title={`Reduce ${teamRow.team} offense -10%`}
                        >
                          ▼
                        </button>
                      </div>

                      <div className="min-w-[30px] text-right leading-none">
                        <div
                          className={`text-[13px] font-bold tabular-nums ${
                            isDark ? "text-slate-200" : "text-slate-800"
                          }`}
                        >
                          {Number.isFinite(teamRow.total) ? fmt1(teamRow.total) : "—"}
                        </div>
                        {!!(teamBoost[teamRow.team] || 0) && (
                          <div
                            className={`text-[9px] tabular-nums mt-0.5 ${
                              (teamBoost[teamRow.team] || 0) > 0
                                ? "text-emerald-500"
                                : "text-rose-500"
                            }`}
                          >
                            {(teamBoost[teamRow.team] || 0) > 0
                              ? `+${teamBoost[teamRow.team]}`
                              : teamBoost[teamRow.team]}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          </div>
        </section>
      ) : null}

<section className={`${T.card} ${cls.card} p-3 mb-3`} aria-label="Optimize">
  {/* Build button and progress stay together. */}
  <div className="flex max-w-full flex-wrap items-center gap-3">
    <button className="h-9 px-6 rounded-md bg-sky-500 text-slate-950 hover:bg-sky-400 text-sm font-semibold shrink-0 disabled:opacity-50" disabled={isOptimizing || loading || meta.loading || !!err || !!meta.err || !rows.length} onClick={optimize}>
      {`Build ${numLineups} lineups`}
    </button>

  <div className="flex min-w-[110px] flex-1 items-center gap-2 max-w-[420px]">
    <div
      className={[
        "flex-1 h-2 rounded-full overflow-hidden",
        isDark ? "bg-slate-800" : "bg-gray-200",
      ].join(" ")}
    >
      <div
        className="h-full rounded-full transition-all duration-300"
        style={{
          width: `${
            (Math.min(progressUI, Math.max(1, Number(numLineups) || 1)) /
              Math.max(1, Number(numLineups) || 1)) *
            100
          }%`,
          background: "#0ea5e9",
        }}
      />
    </div>

    <div
      className={[
        "text-sm min-w-[60px] text-right tabular-nums",
        isDark ? "text-slate-300" : "text-gray-600",
      ].join(" ")}
    >
      {progressUI}/{numLineups}
    </div>
  </div>

  </div>

  <details className={`mt-3 border-t pt-3 ${isDark ? "border-slate-800" : "border-slate-200"}`}>
    <summary className="cursor-pointer select-none text-sm font-semibold">Reset Tools</summary>
    <div className="mt-3 flex flex-wrap gap-2">
    <button className={ghostSmBtnClass} onClick={clearAll}>
      Reset all
    </button>
    <button className={ghostSmBtnClass} onClick={clearExcludesAll}>
      Clear excludes
    </button>
    <button className={ghostSmBtnClass} onClick={clearLocks}>
      Clear locks
    </button>
    <button className={ghostSmBtnClass} onClick={clearBoosts}>
      Clear boosts
    </button>
    <button className={ghostSmBtnClass} onClick={clearMinMax}>
      Clear min/max
    </button>
    </div>
  </details>
</section>

<section className={`${T.card} ${cls.card} mb-6 overflow-hidden`} aria-label="Player pool">
  <div className={`p-3 border-b ${isDark ? "border-slate-800" : "border-slate-200"}`}>
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <h2 className="text-sm font-semibold">Player Pool</h2>
      <span className={`text-xs ${T.subtext}`}>{sortedRows.length.toLocaleString()} slots shown</span>
    </div>
    <div className="mb-3 flex flex-wrap items-center gap-3">
  {/* Slot tabs */}
      <div
        className="flex flex-wrap items-center gap-3"
      >
        {["ALL", cfg.capTag, "FLEX"].map((t) => {
          const active = tagFilter === t;

          return (
            <button
              key={t}
              type="button"
              onClick={() => setTagFilter(t)}
              className={[
                "inline-flex items-center justify-center border-b-2",
                "pb-2 text-xs font-semibold leading-none",
                "transition-all duration-150 select-none",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50",

                active
                  ? isDark
                    ? "border-sky-400 text-sky-400"
                    : "border-sky-400 text-sky-600"
                  : isDark
                    ? "border-transparent text-slate-500 hover:text-slate-100"
                    : "border-transparent text-slate-500 hover:text-slate-900",
              ].join(" ")}
            >
              {t}
            </button>
          );
        })}
      </div>

  {/* RIGHT: Search */}
  <div className="w-full sm:w-auto sm:ml-auto">
    <input
      className={`${inputClass} w-full sm:w-80`}
      placeholder="Search player / team / pos…"
      value={q}
      onChange={(e) => setQ(e.target.value)}
    />
  </div>
    </div>
{/* Quick Clears */}
<div className="mb-1">
  <div className="flex flex-wrap items-center gap-2">
    <label
      className={`${ghostSmBtnClass} flex items-center gap-2 cursor-pointer`}
      title="Hide players without a source projection"
    >
      <input
        type="checkbox"
        checked={hideSiteIdOnly}
        onChange={(e) => setHideSiteIdOnly(e.target.checked)}
      />
      Hide Unprojected
    </label>
    <label className={`${ghostSmBtnClass} flex items-center gap-2 cursor-pointer`}
      title="Hide excluded players, including automatic exclusions">
      <input type="checkbox" checked={hideExcluded}
        onChange={(e) => setHideExcluded(e.target.checked)} />
      Hide Excluded
    </label>
    <label className={`${ghostSmBtnClass} flex items-center gap-2`}
      title="Automatically exclude slots below this boosted points-per-$1,000 value. The cutoff itself is included.">
      Exclude value under
      <input type="number" min="0" step="0.1"
        className={`h-6 w-12 rounded-md border px-1 text-center text-xs outline-none focus:ring-2 focus:ring-sky-500/30 ${T.input}`}
        placeholder="—" value={minValue}
        onChange={(e) => setMinValue(e.target.value)} />
    </label>

    {/* bulk exclude toggles */}
    <button className={ghostSmBtnClass} onClick={toggleExcludeCurrentSlot}>
      {tagFilter === "ALL"
        ? "Exclude ALL slots (toggle)"
        : `Exclude ${tagFilter} players (toggle)`}
    </button>
  </div>
</div>

  </div>
        <div className={tableWrapClass}>
          <div className="max-h-[68vh] overflow-auto">
            <table className={`w-full border-separate ${textSz}`} style={{ borderSpacing: 0 }}>
              <thead className={theadClass}>
                <tr>
                  {TABLE_COLS.map(({ key, label, sortable }) => (
                    <th
                      key={key}
                      className={`${header} whitespace-nowrap ${sortable ? "cursor-pointer" : ""}`}
                      onClick={() => sortable && setSort(key)}
                    >
                      {label}
                      {sortable ? <span className="opacity-60">{sortArrow(key)}</span> : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td className={`${header} text-gray-500`} colSpan={TABLE_COLS.length}>
                      Loading…
                    </td>
                  </tr>
                )}
                {err && (
                  <tr>
                    <td className={`${header} text-red-600`} colSpan={TABLE_COLS.length}>
                      Failed to load: {String(err)}
                    </td>
                  </tr>
                )}
                {!loading &&
                  !err &&
                  sortedRows.map((r) => {
                    const teamTitle = `${NFL_TEAMS[r.team]?.city ?? r.team} ${NFL_TEAMS[r.team]?.nickname ?? ""}`.trim();
                    const oppTitle = `${NFL_TEAMS[r.opp]?.city ?? r.opp} ${NFL_TEAMS[r.opp]?.nickname ?? ""}`.trim();
                    const key = r.key; // Player::CPT/MVP or Player::FLEX
                    return (
                      <tr
                        key={key}
                        className={rowClass}
                      >
                        <td className={cell}>
                          <input
                            type="checkbox"
                            checked={locks.has(tagKey(r.name, r.tag))}
                            onChange={() => toggleLock(r.name, r.tag)}
                          />
                        </td>
                        <td className={cell}>
                          <input
                            type="checkbox"
                            checked={effectiveExcls.has(tagKey(r.name, r.tag))}
                            disabled={valueExcludedKeys.has(r.key)}
                            title={valueExcludedKeys.has(r.key) ? "Excluded by value cutoff. Lower or clear the cutoff to include this slot." : "Exclude this slot"}
                            onChange={() => toggleExclRow(r)}
                          />
                        </td>
                        <td className={cell}>
                          <div className="inline-flex items-center gap-1">
                            <button className={iconBtnClass} title="+3%" onClick={() => bumpBoost(r.name, +1)}>+</button>
                            <span className="w-5 text-center">
                              {Number(boost[r.name] || 0) + (r.pos !== "DST" ? Number(teamBoost[r.team] || 0) : 0)}
                            </span>
                            <button className={iconBtnClass} title="-3%" onClick={() => bumpBoost(r.name, -1)}>–</button>
                          </div>
                        </td>
                        <td className={cell}>{r.tag === "FLEX" ? "FLEX" : cfg.capTag}</td>
                        <td className={`${cell} whitespace-nowrap text-left`}>
                          <div className="inline-flex items-center gap-2">
                            {["QB", "RB", "WR", "TE"].includes(r.pos) ? (
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setGameLogPlayer({ name: r.name, pos: r.pos });
                                }}
                                className="font-semibold text-inherit underline decoration-current/50 underline-offset-2 hover:text-sky-500 hover:decoration-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:rounded"
                                title={`View ${r.name} game log`}
                                aria-haspopup="dialog"
                              >
                                {r.name}
                              </button>
                            ) : <span>{r.name}</span>}
                            {r.siteIdOnly ? (
                              <span
                                className={
                                  "rounded-full border px-2 py-[1px] text-[10px] font-semibold uppercase tracking-wide " +
                                  (isDark
                                    ? "border-amber-400/40 bg-amber-400/10 text-amber-200"
                                    : "border-amber-300 bg-amber-50 text-amber-700")
                                }
                                title="In site IDs but missing a source projection"
                              >
                                no proj
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className={cell}>
                          <TeamPill abbr={r.team} title={teamTitle} />
                        </td>
                        <td className={cell}>
                          <TeamPill abbr={r.opp} title={oppTitle} />
                        </td>
                        <td className={cell}>{r.pos}</td>
                        <td className={`${cell} tabular-nums`}>{fmt0(r.salaryDisplay)}</td>
                        <td className={`${cell} tabular-nums`}>
                          {(() => {
                            const originalProj = Number(r.baseProjDisplay ?? r.projDisplay ?? 0);
                            const activeProj = Number(r.unboostedProjDisplay ?? 0);
                            const playerSteps = Number(boost[r.name] || 0);
                            const offenseSteps = r.pos !== "DST" ? Number(teamBoost[r.team] || 0) : 0;
                            const boostedProj = activeProj * (1 + 0.03 * playerSteps + 0.10 * offenseSteps);
                            const isUp = boostedProj > originalProj;
                            const isDown = boostedProj < originalProj;
                            const projStyle = isUp
                              ? {
                                  backgroundColor: isDark ? "rgba(16,185,129,0.18)" : "#ecfdf5",
                                  borderColor: isDark ? "#34d399" : "#10b981",
                                  color: isDark ? "#a7f3d0" : "#047857"
                                }
                              : isDown
                              ? {
                                  backgroundColor: isDark ? "rgba(239,68,68,0.18)" : "#fef2f2",
                                  borderColor: isDark ? "#f87171" : "#ef4444",
                                  color: isDark ? "#fecaca" : "#b91c1c"
                                }
                              : undefined;
                            return (
                              <div className="inline-flex items-center justify-center gap-1">
                                <input
                                  type="number"
                                  step="0.1"
                                  inputMode="decimal"
                                  className={`${tableInputClass} text-center tabular-nums`}
                                  style={{ width: 62, ...(projStyle || {}) }}
                                  value={r.hasCustomProj ? String(r.customProjValue ?? "") : fmt1(boostedProj)}
                                  placeholder={fmt1(originalProj)}
                                  onChange={(e) => setPlayerCustomProj(r.name, r.tag, e.target.value)}
                                  title={
                                    r.hasCustomProj
                                      ? `Custom projection. Original: ${fmt1(originalProj)} | Boosted: ${fmt1(boostedProj)}`
                                      : `Original: ${fmt1(originalProj)} | Boosted: ${fmt1(boostedProj)}. Type to set a projection before boosts.`
                                  }
                                />
                                {r.hasCustomProj && (playerSteps !== 0 || offenseSteps !== 0) ? (
                                  <span className="text-xs tabular-nums" title="Projection after player and team boosts">
                                    → {fmt1(boostedProj)}
                                  </span>
                                ) : null}
                                {r.hasCustomProj ? (
                                  <button
                                    type="button"
                                    className={iconBtnClass}
                                    onClick={() => resetPlayerCustomProj(r.name, r.tag)}
                                    title="Reset this projection"
                                  >
                                    ↺
                                  </button>
                                ) : null}
                              </div>
                            );
                          })()}
                        </td>
                        <td className={`${cell} tabular-nums`} title="Boosted projection per $1,000 of slot salary">{fmt1(r.valueDisplay)}</td>
                        <td className={`${cell} tabular-nums`}>{fmt1(r.floorDisplay)}</td>
                        <td className={`${cell} tabular-nums`}>{fmt1(r.ceilDisplay)}</td>
                        <td className={`${cell} tabular-nums`}>{r.pownDisplay == null ? "—" : fmt1(r.pownDisplay * 100)}</td>
                        <td className={`${cell} tabular-nums`}>{r.optDisplay == null ? "—" : fmt1(r.optDisplay * 100)}</td>

                      {/* Per-slot Min/Max wired to key = Player::CPT|MVP|FLEX */}
                      <td className={cell}>
                        <div className="inline-flex items-center gap-1">
                          <button
                            className={iconBtnClass}
                            onClick={() =>
                              setMinPctTag((m) => ({
                                ...m,
                                [key]: clamp((num(m[key]) || 0) - 5, 0, 100),
                              }))
                            }
                            title="-5%"
                          >
                            –
                          </button>

                          <input
                            className={`${tableInputClass} text-center`}
                            style={{ width: 48 }}
                            value={String(minPctTag[key] ?? "")}
                            onChange={(e) => setMinPctTag((m) => ({ ...m, [key]: e.target.value }))}
                            placeholder="—"
                          />

                          <button
                            className={iconBtnClass}
                            onClick={() =>
                              setMinPctTag((m) => ({
                                ...m,
                                [key]: clamp((num(m[key]) || 0) + 5, 0, 100),
                              }))
                            }
                            title="+5%"
                          >
                            +
                          </button>
                        </div>
                      </td>

                      <td className={cell}>
                        <div className="inline-flex items-center gap-1">
                          <button
                            className={iconBtnClass}
                            onClick={() =>
                              setMaxPctTag((m) => ({
                                ...m,
                                [key]: clamp((num(m[key]) || 100) - 5, 0, 100),
                              }))
                            }
                            title="-5%"
                          >
                            –
                          </button>

                          <input
                            className={`${tableInputClass} text-center`}
                            style={{ width: 48 }}
                            value={String(maxPctTag[key] ?? "")}
                            onChange={(e) => setMaxPctTag((m) => ({ ...m, [key]: e.target.value }))}
                            placeholder="—"
                          />

                          <button
                            className={iconBtnClass}
                            onClick={() =>
                              setMaxPctTag((m) => ({
                                ...m,
                                [key]: clamp((num(m[key]) || 100) + 5, 0, 100),
                              }))
                            }
                            title="+5%"
                          >
                            +
                          </button>
                        </div>
                      </td>

                      <td className={`${cell} tabular-nums`}>
                        {usagePct[r.key] != null ? fmt1(usagePct[r.key]) : "—"}
                      </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
</section>

      {/* === Lineup Results + Exposure Row === */}
      {!!lineups.length && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
          {/* Lineup Results */}
          <div className="xl:col-span-2">
            <div className={`${cls.card} ${T.card} p-3`}>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-base font-bold">Lineups ({lineups.length})</h2>
                <div className="flex items-center gap-2">
                  <button
                    className={ghostBtnClass}
                    onClick={() =>
                      downloadShowdownCSV({ lineups, site, rows, slate: activeSlate })
                    }
                  >
                    Export CSV (IDs)
                  </button>

                  {/* NEW: Detailed export (adds Salary, Total Proj/Floor/Ceil, Total pOWN%/Opt%) */}
                  <button
                    className={ghostBtnClass}
                    onClick={() =>
                      downloadShowdownCSV({ lineups, site, rows, slate: activeSlate, detailed: true })
                    }
                  >
                    Export CSV (Detailed)
                  </button>
                </div>
              </div>
              <div className="overflow-auto max-h-[440px]">
                <table className={`min-w-full text-[12px] border-separate ${tableText}`} style={{ borderSpacing: 0 }}>
                  <thead>
                    <tr>
                      <th
                        className={`${header} sticky top-0 z-20 ${
                          isDark ? "bg-slate-800" : "bg-gray-50"
                        }`}
                      >
                        #
                      </th>

                      <th
                        className={`${header} sticky top-0 z-20 ${
                          isDark ? "bg-slate-800" : "bg-gray-50"
                        }`}
                      >
                        Salary
                      </th>

                      <th
                        className={`${header} sticky top-0 z-20 ${
                          isDark ? "bg-slate-800" : "bg-gray-50"
                        }`}
                      >
                        {`Total pOWN%${
                          ownershipAvailable && String(lineupPownCap).trim() !== ""
                            ? ` (≤ ${lineupPownCap})`
                            : ""
                        }`}
                      </th>

                      <th
                        className={`${header} sticky top-0 z-20 ${
                          isDark ? "bg-slate-800" : "bg-gray-50"
                        }`}
                      >
                        Total {totalLabel}
                      </th>

                      <th
                        className={`${header} sticky top-0 z-20 text-left ${
                          isDark ? "bg-slate-800" : "bg-gray-50"
                        }`}
                      >
                        Players
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineups.map((L, i) => {
                      const lineupRates = showdownLineupRates(L, new Map(rows.map((r) => [r.name, r])));
                      const totalPown = lineupRates.totalPown;
                      const displayPlayers = orderShowdownNamesByFlexSalary(
                        L.players,
                        new Map(rows.map((r) => [r.name, r]))
                      );
                      return (
                        <tr key={i} className={stripeRow}>
                          <td className={cell}>{i + 1}</td>
                          <td className={`${cell} tabular-nums`}>{fmt0(L.salary)}</td>
                          <td className={`${cell} tabular-nums`}>{lineupRates.ownComplete ? fmt1(totalPown) : "—"}</td>
                          <td className={`${cell} tabular-nums`}>{fmt1(L.total)}</td>
                          <td className={`${cell} leading-snug text-left`}>
                            <span className="break-words">
                              {displayPlayers[0]} ({cfg.capTag}) • {displayPlayers.slice(1).join(" • ")}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Exposure / Teams / Stacks Panel (single) */}
          <div>
            <div className={`${cls.card} ${T.card}`}>
              <div className="border-b border-gray-200 flex gap-2 p-2 text-sm">
                {["Exposure", "Teams", "Stacks"].map((t) => (
                  <button
                    key={t}
                    onClick={() => setTab(t)}
                    className={`px-3 py-1.5 rounded-md transition-colors ${
                      tab === t
                        ? "bg-blue-600 text-white"
                        : isDark
                          ? "text-slate-200 hover:bg-white/10 hover:text-white"
                          : "text-slate-700 hover:bg-slate-200 hover:text-slate-950"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <div className="p-3 max-h-[700px] overflow-auto">
                {tab==="Exposure" && (
                  <>
                  <div
                    className={`mb-2 inline-flex rounded-full shadow-sm ring-1 overflow-hidden ${T.pill}`}
                  >
                    {["ALL", cfg.capTag, "FLEX"].map((t) => (
                      <button
                        key={t}
                        onClick={() => setExpScope(t)}
                        className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                          expScope === t
                            ? "bg-blue-600 text-white"
                            : isDark
                              ? "text-slate-100 hover:bg-white/10 hover:text-white"
                              : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                    <table className={`w-full ${textSz} ${tableText} ${tableText}`}>
                      <thead>
                        <tr>
                          <th className={header}>Slot</th>
                          <th className={header + " text-left"}>Player</th>
                          <th className={header}>Count</th>
                          <th className={header}>Exposure %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {exposures.map((r, i) => (
                          <tr key={`${r.name}-${r.slot}-${i}`} className={stripeRow}>
                            <td className="px-2 py-1 text-center">{slotLabel(r.slot)}</td>
                            <td className="px-2 py-1 text-left">{r.name}</td>
                            <td className="px-2 py-1 text-center">{r.count}</td>
                            <td className="px-2 py-1 text-center">{fmt1(r.pct)}</td>
                          </tr>
                        ))}
                        {!exposures.length && (
                          <tr>
                            <td className="px-2 py-1 text-center text-gray-500" colSpan={4}>
                              No lineups yet.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </>
                )}
                {tab==="Teams" && (
                  <table className={`w-full ${textSz} ${tableText} ${tableText}`}>
                    <thead>
                      <tr>
                        <th className={header}>Team</th>
                        <th className={header}>Count</th>
                        <th className={header}>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamExposure.map((t, i) => (
                        <tr key={i} className={stripeRow}>
                          <td className="px-2 py-1 text-center">
                            <TeamPill abbr={t.team} />
                          </td>
                          <td className="px-2 py-1 text-center">{t.count}</td>
                          <td className="px-2 py-1 text-center">{fmt1(t.pct)}</td>
                        </tr>
                      ))}
                      {!teamExposure.length && (
                        <tr>
                          <td className="px-2 py-1 text-center text-gray-500" colSpan={3}>
                            —
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
                {tab==="Stacks" && (
                  <table className={`w-full ${textSz} ${tableText} ${tableText}`}>
                    <thead>
                      <tr>
                        <th className={header}>Shape</th>
                        <th className={header}>Count</th>
                        <th className={header}>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stackShapes.map((s, i) => (
                        <tr key={i} className={stripeRow}>
                          <td className="px-2 py-1 text-center">{s.shape}</td>
                          <td className="px-2 py-1 text-center">{s.count}</td>
                          <td className="px-2 py-1 text-center">{fmt1(s.pct)}</td>
                        </tr>
                      ))}
                      {!stackShapes.length && (
                        <tr>
                          <td className="px-2 py-1 text-center text-gray-500" colSpan={3}>
                            —
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === Lineup Cards Row === */}
      {!!lineups.length && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {lineups.map((L, idx) => {
            const rowsMap = new Map(rows.map((r) => [r.name, r]));
            const displayPlayers = orderShowdownNamesByFlexSalary(L.players, rowsMap);
            const lineupRates = showdownLineupRates(L, rowsMap);
            let totalPown = 0;

            const geoMeanPown = geoMeanPct(
              displayPlayers
                .map((n, i) => {
                  const r = rowsMap.get(n);
                  if (!r) return null;
                  return {
                    pown: ((i === 0 ? r.cap_pown : r.pown) || 0) * 100,
                  };
                })
                .filter(Boolean),
              (x) => x.pown
            );

            return (
              <div key={idx} className={`${cls.card} ${T.card} overflow-hidden`}>
                <div className="px-3 pt-3 pb-2">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-base font-semibold">
                        Lineup #{idx + 1}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <div className={`text-[12px] font-medium ${isDark ? "text-slate-300" : "text-gray-600"}`}>
                        Geo Mean% {lineupRates.ownComplete ? fmt1(geoMeanPown) : "—"}
                      </div>

                      <img
                        src={cfg.logo}
                        alt={cfg.label}
                        title={cfg.label}
                        className="w-5 h-5 object-contain shrink-0"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    </div>
                  </div>
                </div>

                <table className={`w-full ${textSz} ${tableText}`}>
                  <thead>
                    <tr>
                      <th className={header}>Slot</th>
                      <th className={header + " text-left"}>Player</th>
                      <th className={header}>pOWN%</th>
                      <th className={header}>Proj</th>
                      <th className={header}>Sal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayPlayers.map((n, i) => {
                      const metaRow = rowsMap.get(n);

                      if (!metaRow) {
                        return (
                          <tr key={`${n}-${i}`} className={stripeRow}>
                            <td className={cell}>{i === 0 ? cfg.capTag : "FLEX"}</td>
                            <td className="px-2 py-1 text-left">{n}</td>
                            <td className={cell}>—</td>
                            <td className={cell}>—</td>
                            <td className={cell}>—</td>
                          </tr>
                        );
                      }

                      const proj = i === 0 ? metaRow.cap_proj : metaRow.proj;
                      const sal = i === 0 ? metaRow.cap_salary : metaRow.salary;
                      const pownPct = ((i === 0 ? metaRow.cap_pown : metaRow.pown) || 0) * 100;
                      totalPown += pownPct;

                      return (
                        <tr key={`${n}-${i}`} className={stripeRow}>
                          <td className={cell}>{i === 0 ? cfg.capTag : "FLEX"}</td>
                          <td className="px-2 py-1 text-left flex items-center gap-1">
                            {!!metaRow.team && (
                              <img
                                src={`/logos/nfl/${metaRow.team}.svg`}
                                alt={metaRow.team}
                                className="w-4 h-4 opacity-80 shrink-0"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = `/logos/nfl/${metaRow.team}.png`;
                                }}
                              />
                            )}
                            <span>{n}</span>
                          </td>
                          <td className={cell}>{(i === 0 ? metaRow.cap_pown : metaRow.pown) == null ? "—" : fmt1(pownPct)}</td>
                          <td className={cell}>{fmt1(proj)}</td>
                          <td className={cell}>{fmt0(sal)}</td>
                        </tr>
                      );
                    })}

                    <tr className={totalsRow}>
                      <td className={cell}>Totals</td>
                      <td className="px-2 py-1"></td>
                      <td className={cell}>{lineupRates.ownComplete ? fmt1(totalPown) : "—"}</td>
                      <td className={cell}>{fmt1(L.total)}</td>
                      <td className={cell}>{fmt0(L.salary)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}
      {gameLogPlayer ? (
        <OptimizerGameLogModal
          player={gameLogPlayer.name}
          position={gameLogPlayer.pos}
          preparedRows={preparedGameLogRows}
          loading={gameLogsLoading}
          error={gameLogsError}
          onClose={() => setGameLogPlayer(null)}
          isDark={isDark}
          gameLogYear={gameLogYear}
          gameLogYears={OPT_GAME_LOG_YEARS}
          onGameLogYearChange={setGameLogYear}
        />
      ) : null}
    </div>
  );
}

/* ---------------------- Player group picker (UI) -------------------- */
function GroupPicker({ allPlayers, selected, onToggle, inputClass, isDark }) {
  const [filter, setFilter] = useState("");
  const ic = inputClass || cls.input;
  const itemCls = isDark
    ? "px-2 py-1 rounded-md text-slate-100 hover:bg-slate-800/80"
    : "px-2 py-1 rounded-md text-slate-800 hover:bg-slate-100";
  const list = useMemo(
    () => allPlayers.filter((n) => n.toLowerCase().includes(filter.toLowerCase())),
    [allPlayers, filter]
  );
  return (
    <div className="w-full">
      <input
        className={`${ic} w-full mb-2`}
        placeholder="Search player…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />
      <div className={`max-h-40 overflow-auto rounded-md p-1 border ${isDark ? "border-slate-700 bg-slate-950/60" : "border-gray-200 bg-white"}`}>
        {list.map((n) => (
          <label key={n} className={`flex items-center gap-2 px-2 py-1 text-sm cursor-pointer rounded-md ${isDark ? "text-slate-100 hover:bg-slate-800/70" : "text-slate-800 hover:bg-gray-50"}`}>
            <input type="checkbox" checked={selected.includes(n)} onChange={() => onToggle(n)} />
            <span>{n}</span>
          </label>
        ))}
        {!list.length && <div className={`text-xs px-2 py-1 ${isDark ? "text-slate-400" : "text-gray-500"}`}>No matches</div>}
      </div>
    </div>
  );
}

/* ---------------------------- CSV (IDs) ---------------------------- */
function orderShowdownNamesByFlexSalary(names, rowsMap) {
  const lineup = Array.isArray(names) ? names.filter(Boolean) : [];
  if (lineup.length <= 1) return lineup;

  const captainOrMvp = lineup[0];
  const flexes = lineup
    .slice(1)
    .map((name, originalIndex) => ({
      name,
      originalIndex,
      salary: Number(rowsMap.get(name)?.salary || 0),
    }))
    .sort((a, b) => b.salary - a.salary || a.originalIndex - b.originalIndex)
    .map((item) => item.name);

  return [captainOrMvp, ...flexes];
}

function orderPlayersForSiteShowdown(names, rowsMap) {
  return orderShowdownNamesByFlexSalary(names, rowsMap)
    .map((n) => rowsMap.get(n))
    .filter(Boolean);
}

